import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Onboarding from "./components/Onboarding";
import ErrorBoundary from "./components/ErrorBoundary";
import KidModeShell from "./experiments/kids-mode/KidModeShell";
import { KidRequestsInbox } from "./experiments/kids-mode/KidRequestsInbox";
import Home from "./components/Home";
import Community from "./components/Community";
import Plan from "./components/Plan";
import CreateMenu from "./components/CreateMenu";
import EatQuickSheet from "./components/EatQuickSheet";
import IncomingShareSheet from "./components/IncomingShareSheet";
import SignIn from "./components/SignIn";
import NewPasswordSheet from "./components/NewPasswordSheet";
import { stashInvite, takeInvite, inviteCodeFromUrl } from "./lib/inviteStash";
import { syncRegionFromProfile } from "./lib/useShoppingRegion";
import { setActiveStore } from "./lib/useActiveStore";
import OnboardingTour from "./components/OnboardingTour";
import Settings from "./components/Settings";
import FindFriendsSheet from "./components/FindFriendsSheet";
import SocialPage from "./components/SocialPage";
import { useTeamLiveActivity } from "./lib/useTeamLiveActivity";
import { useActivityPushTokens } from "./lib/useActivityPushTokens";
import { BoodaTimer } from "./lib/boodaTimer";
import { useKeyboardOffset } from "./lib/useKeyboardOffset";
import AdminPanel from "./components/AdminPanel";
import NotificationsPanel from "./components/NotificationsPanel";
import UserProfile from "./components/UserProfile";
import RecipeBook from "./components/RecipeBook";
import ShoppingQuickAdd from "./components/ShoppingQuickAdd";
import WhatsNewNotification from "./components/WhatsNewNotification";
import ReleaseNotesModal from "./components/ReleaseNotesModal";
import LevelUpCeremony from "./components/LevelUpCeremony";
import BackgroundMusic from "./components/BackgroundMusic";
import WeatherFX from "./components/WeatherFX";
import RadioRewards from "./components/RadioRewards";
import { playSfx, preloadSfx } from "./lib/sfx";
import XpToastStack from "./components/XpToastStack";
import CookMode from "./components/CookMode";
import RecipeReviseSheet from "./components/RecipeReviseSheet";
import CookBanner from "./components/CookBanner";
import LapsedCookBanner from "./components/LapsedCookBanner";
import MCMCookingShowcase from "./experiments/mcm-cooking/Showcase";
import MCMKitchenScreen, { MCMAddDraftSheet } from "./experiments/mcm-cooking/KitchenScreen";
import { ThemeProvider as MCMThemeProvider } from "./lib/theme";
import { WarmBackdrop } from "./experiments/mcm-cooking/primitives";
import SuccessOverlay from "./experiments/mcm-cooking/SuccessOverlay";
// Classic ItemCard import dropped — not mounted at App level. Only
// reachable via Kitchen.jsx's Shop Mode fallback, which imports it
// directly. App lives entirely on MCMItemCard now.
import { MCMItemCard } from "./experiments/mcm-cooking/MCMItemCard";
// Shop Mode — full rebuild on the kitchen-inflow primitives.
// Three-phase flow (scan → resolve → checkout) with name-preserving
// classifier. Replaces the legacy ShopMode + ShopModeCheckout +
// ShopModePlacementWizard trio.
import ShopMode from "./components/shop/ShopMode";
// Phase 3 step 4 — Receipt surfaces lifted similarly.
import ReceiptView from "./components/ReceiptView";
import ReceiptHistoryModal from "./components/ReceiptHistoryModal";
import TripHistoryModal from "./components/TripHistoryModal";
import TripDetailModal from "./components/TripDetailModal";
import { MCMShoppingScreen } from "./experiments/mcm-cooking/MCMShoppingScreen";
import { MCMShoppingAdd } from "./experiments/mcm-cooking/MCMShoppingAdd";
import { useActiveCookSession } from "./lib/useActiveCookSession";
import { useActiveCookSessions } from "./lib/useActiveCookSessions";
import { useUserRecipes } from "./lib/useUserRecipes";
import { runBackgroundRecipeGen, retryBackgroundRecipeGen } from "./lib/backgroundRecipeGen";
import { stripBogusReheat } from "./lib/doneness";
import { useScheduledMeals } from "./lib/useScheduledMeals";
import { findRecipe } from "./data/recipes";
import SchedulePicker from "./components/SchedulePicker";
import { useWhatsNew } from "./lib/useWhatsNew";
import { useAuth } from "./lib/useAuth";
import { useProfile } from "./lib/useProfile";
import { useAvatars } from "./lib/useAvatars";
import { usePantry } from "./lib/usePantry";
import { useShoppingList } from "./lib/useShoppingList";
import { useMonthlySpend } from "./lib/useMonthlySpend";
// (ReceiptHistoryModal imported above with other Phase 3 lifts)
import { useRelationships } from "./lib/useRelationships";
import { useNotifications } from "./lib/useNotifications";
import { ToastProvider, useToast } from "./lib/toast";
import { supabase } from "./lib/supabase";
import { IngredientInfoProvider } from "./lib/useIngredientInfo";
import { BrandNutritionProvider } from "./lib/useBrandNutrition";
import { FONT, COLOR } from "./lib/tokens";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";

// Four regular tabs + a floating ➕ Quick Cook button slotted between
// slot 2 (COURSES) and slot 3 (CALENDAR). The ➕ is not a tab — it opens
// an overlay chooser, so the currently-active tab stays active underneath.
// Dock nav. Emoji axis is intentionally absent — per the user's
// no-emojis-in-UI standing direction; custom SVG iconography may
// land later but the label alone is the source of truth for now.
const NAV = [
  { id:"home",    label:"Home"     },
  { id:"community", label:"Community"  },
  { id:"plan",    label:"Calendar" },
  { id:"pantry",  label:"Kitchen"  },
];

// Dock tab — label-only (no emoji per the no-emojis-in-UI standing
// direction). The blob indicator is hoisted to the dock level (see
// DockIndicator below) so two trailing/leading blobs can stretch
// across the dock through a shared SVG goo filter, which requires
// them to live in a single goo-filtered layer. A per-button indicator
// can't do that — its DOM is scoped to its own button.
//
// Each button forwards its DOM ref via buttonRef so DockNav can
// measure positions and target the indicator. Press feedback stays on
// the button so it still feels listened-to.
function DockTab({ id, label, active, onSelect, buttonRef }) {
  const handleClick = () => {
    // Tab-specific sfx fire ONLY when switching INTO the tab (not on
    // re-tap of the active tab) so the sound reads as a transition,
    // not feedback. Add new entries here as more tab sounds drop in.
    if (!active) {
      if (id === "pantry") playSfx("kitchen-tab");
      else if (id === "home") playSfx("home-tab");
    }
    onSelect(id);
  };
  return (
    <motion.button
      ref={buttonRef}
      onClick={handleClick}
      transition={{ type: "spring", stiffness: 600, damping: 30 }}
      style={{
        position: "relative",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "8px 12px",
        borderRadius: 999,
        zIndex: 1,
      }}
    >
      <span style={{
        fontFamily: FONT.forma,
        fontSize: 12,
        fontWeight: 600,
        color: active ? "#FFF8EE" : COLOR.muted,
        letterSpacing: "0.10em",
        position: "relative",
        transition: "color 240ms cubic-bezier(0.32, 0.72, 0, 1)",
      }}>
        {label.toUpperCase()}
      </span>
    </motion.button>
  );
}

// Dock indicator — the actual blob. Two motion.divs animate to the
// active button's bounding box with different spring stiffness, so
// one is always the "leading edge" and the other "trails." Both share
// the same gradient and live inside a `filter: url(#dockGoo)` layer;
// the goo filter is a Gaussian blur followed by a sharp colorMatrix
// threshold (the classic CSS metaball trick) which fuses overlapping
// translucent shapes into a single solid blob. As the trail lags
// behind the lead, the goo merges them into one continuously stretched
// pill — leading edge already at the destination, trailing edge still
// half a tab behind. When the trail catches up the blob shrinks back
// to a single pill at the new position. That's the "tail follows" feel.
//
// Why two blobs not one + scaleX keyframes:
//   - scaleX from center stretches both ways, never anchored to
//     leading-edge-arrives-first
//   - keyframes on `width` aren't direction-aware (going left vs right
//     would need flipped origins)
//   - two springs is direction-AGNOSTIC: whichever way you tap, the
//     stiffer blob arrives first and the softer one trails — the goo
//     filter merges them either way
//
// initial=false so the very first render snaps to the active tab's
// position without animating-in from 0,0.
function DockIndicator({ rect }) {
  if (!rect) return null;
  const blobBase = {
    position: "absolute",
    left: 0, top: 0,
    background: "linear-gradient(180deg, #2F8F83 0%, #267770 100%)",
    boxShadow: "0 6px 14px rgba(47,143,131,0.32)",
    borderRadius: 999,
    pointerEvents: "none",
  };
  const target = {
    x: rect.x, y: rect.y,
    width: rect.width, height: rect.height,
  };
  // Height & y snap fast (no vertical stretching desired — the dock is
  // a single row). Only x and width get the lead/trail split so the
  // morph stretches purely horizontally.
  const fastVertical = { type: "spring", stiffness: 800, damping: 50 };
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        // The goo filter — see <DockGooFilter /> for the SVG def.
        // Applied to this layer only so the merged-blob effect is
        // limited to the indicator and doesn't blur the buttons above.
        filter: "url(#dockGoo)",
        WebkitFilter: "url(#dockGoo)",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {/* Trailing blob — softer spring lags behind during motion.
          On going-right taps this is the LEFT edge; on going-left
          taps it's the RIGHT edge. Direction-agnostic because both
          blobs animate to the SAME target — only spring tuning
          decides which arrives first. */}
      <motion.div
        initial={false}
        animate={target}
        transition={{
          x:      { type: "spring", stiffness: 180, damping: 20 },
          width:  { type: "spring", stiffness: 180, damping: 20 },
          y:      fastVertical,
          height: fastVertical,
        }}
        style={blobBase}
      />
      {/* Leading blob — stiffer spring, arrives first. The lead/trail
          gap mid-flight is what the goo filter melts into a stretched
          pill bridging old + new positions. */}
      <motion.div
        initial={false}
        animate={target}
        transition={{
          x:      { type: "spring", stiffness: 600, damping: 38 },
          width:  { type: "spring", stiffness: 600, damping: 38 },
          y:      fastVertical,
          height: fastVertical,
        }}
        style={blobBase}
      />
    </div>
  );
}

// SVG goo filter — Gaussian blur + sharp colorMatrix threshold. The
// matrix's last row (0 0 0 22 -10) cranks alpha contrast: anything
// below ~45% alpha after the blur clips to transparent, anything above
// clips to fully opaque. Result: blurred edges between two overlapping
// blobs become one sharp shared boundary, which is the metaball effect.
// stdDeviation tuned tight (3px) — bigger blurs pull the gradient out
// of focus on a 38px-tall pill. Mounted at the dock level so the
// filter only applies to the indicator, never the buttons or text.
function DockGooFilter() {
  return (
    <svg
      aria-hidden
      style={{ position: "absolute", width: 0, height: 0, pointerEvents: "none" }}
    >
      <defs>
        <filter id="dockGoo" x="-10%" y="-50%" width="120%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feColorMatrix
            in="blur"
            mode="matrix"
            values="1 0 0 0 0
                    0 1 0 0 0
                    0 0 1 0 0
                    0 0 0 22 -10"
            result="goo"
          />
          {/* Composite goo back over the source so the gradient inside
              the blob keeps its detail — the goo edge is what merges,
              the interior stays crisp. */}
          <feComposite in="SourceGraphic" in2="goo" operator="atop" />
        </filter>
      </defs>
    </svg>
  );
}

// pageShell is now transparent so the root WarmBackdrop (mounted in
// AuthedApp's return below) shows through every tab. Page-level dark
// fills inside individual screens (Kitchen.jsx's `#0f0f0f`,
// CookMode's `#111`, etc.) still paint over the backdrop until those
// files are swept to use tokens — see plan Phase 3.
const pageShell = {
  minHeight: "100vh",
  background: "transparent",
  color: "#1E1E1E",
  maxWidth: 480,
  margin: "0 auto",
  position: "relative",
};

function LoadingSplash() {
  return (
    <MCMThemeProvider>
      <WarmBackdrop />
      <div style={{ ...pageShell, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <img
          src="/boodalogo.png"
          alt="Booda"
          style={{ width: 200, height: "auto", animation: "pulse 1.2s ease-in-out infinite" }}
        />
        <style>{`@keyframes pulse { 0%,100%{opacity:.4} 50%{opacity:1} }`}</style>
      </div>
    </MCMThemeProvider>
  );
}

// Best-effort name from whatever the auth provider gave us.
// Google returns full_name / given_name / family_name; magic-link gives nothing.
function nameFromAuth(user) {
  const md = user?.user_metadata || {};
  return (
    md.full_name ||
    md.name ||
    [md.given_name, md.family_name].filter(Boolean).join(" ") ||
    null
  );
}

// Experimental MCM/liquid-glass cooking-app showcase, reachable via
// `#mcm-cooking` in the URL hash. Kept entirely separate from the
// main dark app — it short-circuits App() before any auth / data
// hooks run, so it's a pure design sandbox.
function useHashRoute() {
  const [hash, setHash] = useState(() =>
    typeof window !== "undefined" ? window.location.hash : ""
  );
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return hash;
}

// Module-level holders for handoffs from MainApp's native deep-link
// listener into AuthedApp's React state. Both happen because MainApp
// (which mounts the native appUrlOpen listener at boot) and AuthedApp
// (which owns the relevant state) are sibling components with no
// shared scope. AuthedApp publishes here when its closures are in
// scope; MainApp's listener calls whichever's current at fire-time.
let inviteCallbackHolder = null;
let sharedUrlCallbackHolder = null;
// Cold-start share stash — a share deep link can fire before AuthedApp
// mounts (app cold-launched BY the share). Invites already had a stash;
// share URLs used to be dropped with a console.warn here, which was
// half of "share a TikTok → nothing happens." Module scope is enough:
// the App Group dead-drop (consumePendingShare) covers cross-process.
let stashedShareUrl = null;
// Incoming recipe-share (booda.app/r/<token> universal link) — same
// holder + cold-start stash pattern as the two above.
let incomingShareCallbackHolder = null;
let stashedShareToken = null;

export default function App() {
  const hash = useHashRoute();
  if (hash === "#mcm-cooking") return <MCMCookingShowcase />;

  // Root catch-all: any render throw anywhere in the app surfaces the
  // error verbatim instead of a silent white screen. Inner boundaries
  // (e.g. kids-mode) catch first and offer a contextual recovery; this
  // is the backstop for everything else.
  return (
    <ErrorBoundary label="app">
      <MainApp />
    </ErrorBoundary>
  );
}

function MainApp() {
  const { user, loading: authLoading, recoveryPending, clearRecovery } = useAuth();
  const { profile, loading: profileLoading, upsert: upsertProfile, patchLocal: patchProfile } =
    useProfile(user?.id);

  // Invite-link boot stash — capture ?invite= / /invite/CODE from the
  // URL BEFORE auth resolves. A brand-new invitee signs up and walks
  // through onboarding before anything can consume the code; the web
  // Google-OAuth redirect even drops the URL entirely. AuthedApp
  // drains the stash on mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const code = inviteCodeFromUrl(window.location.href);
    if (code) stashInvite(code);
  }, []);

  // Password-recovery landing. Two entry paths converge here: the
  // web email-link tap (PASSWORD_RECOVERY auth event → recoveryPending)
  // and the typed 6-digit recovery code on the sign-in screen (which
  // stashes a localStorage flag because SignIn unmounts the instant
  // the session lands). Either way: show the new-password sheet over
  // whatever renders next.
  const [showRecoverySheet, setShowRecoverySheet] = useState(false);
  useEffect(() => {
    if (!user) return;
    let stashed = false;
    try {
      stashed = localStorage.getItem("booda:recoveryPending") === "1";
      if (stashed) localStorage.removeItem("booda:recoveryPending");
    } catch { /* noop */ }
    if (recoveryPending || stashed) {
      setShowRecoverySheet(true);
      clearRecovery();
    }
  }, [user, recoveryPending, clearRecovery]);

  // Active kid context (kid-as-subset-of-parent rework). When set,
  // the parent's session enters Kid Mode for that profile.id. Cleared
  // by KidParentPanel's "Exit Kids Mode" — no sign-out involved.
  // Declared HERE (not lower down with the rest of the UI state)
  // because the kid-mode early-return below reads it before reaching
  // the bulk of state declarations.
  //
  // PERSISTED to localStorage so closing + reopening the app keeps
  // the same kid signed in (instead of dumping back to the parent
  // home screen every cold boot). Only cleared by the explicit
  // "Exit Kids Mode" action in KidParentPanel, which sets it to null
  // → the setter wipes the localStorage key in the same step.
  const [activeKidId, _setActiveKidIdRaw] = useState(() => {
    try { return localStorage.getItem("booda:activeKidId") || null; }
    catch { return null; }
  });
  const setActiveKidId = useCallback((id) => {
    _setActiveKidIdRaw(id);
    try {
      if (id) localStorage.setItem("booda:activeKidId", id);
      else    localStorage.removeItem("booda:activeKidId");
    } catch {}
  }, []);

  // OAuth deep-link bridge for the native iOS app (Capacitor). The
  // sign-in flow opens Safari, the user authenticates with Google,
  // Supabase redirects to app.booda.ios://auth-callback — which
  // iOS routes back into the app via this listener instead of leaving
  // the user stranded in Safari. We try PKCE code exchange first,
  // then fall back to implicit-flow hash-fragment parsing if the URL
  // looks like an old-style #access_token=... redirect.
  // No-op on web (Capacitor.isNativePlatform() returns false there).
  //
  // Module-level holder for the consumeInviteCode callback (declared
  // far below in AuthedApp, a sibling component). The Capacitor
  // appUrlOpen listener mounts in MainApp at boot — before AuthedApp
  // even exists — so we can't share state via React. AuthedApp writes
  // to inviteCallbackHolder once it has the callback in scope; this
  // listener reads it lazily at fire-time.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let removeFn = null;
    let removeAppState = null;

    // Token refresh is driven by app state on native. autoRefreshToken
    // uses a setInterval, which iOS FREEZES while the app is suspended.
    // If we're backgrounded past the access-token lifetime (~1h), the
    // refresh tick never fires; on resume, until a refresh succeeds,
    // supabase-js has no valid session and REST calls fall back to the
    // ANON key → server-side auth.uid() is null → every RLS-protected
    // write fails with 42501 ("new row violates row-level security
    // policy"). It self-heals once a refresh lands, which is why it
    // reads as "random save failures." Belt-and-braces: persistDiff in
    // useSyncedList.js also gates each flush on getSession() with
    // backoff, since its microtask/thawed-timer flushes can beat the
    // startAutoRefresh() call below.
    //
    // startAutoRefresh() runs an immediate refresh tick (refreshing now
    // if the token is expired/near-expiry) and then resumes the
    // interval, so calling it on resume closes the stale-token window.
    // stopAutoRefresh() on background avoids the frozen-timer churn.
    // This is Supabase's prescribed pattern for React Native / Capacitor.
    CapApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        supabase.auth.startAutoRefresh();
      } else {
        supabase.auth.stopAutoRefresh();
      }
    }).then((handle) => { removeAppState = handle.remove.bind(handle); });

    // (REMOVED 2026-05-06) Global focusin scroll-into-view handler —
    // the smooth-scroll fired 280ms after every focus event, which
    // collided with rapid typing on iOS WKWebView and caused
    // dropped keystrokes (especially the space bar). ModalSheet has
    // its own focus-aware scroll for in-sheet inputs; plain page
    // inputs handle themselves via the viewport meta + KeyboardResize
    // body mode set in capacitor.config.ts.

    CapApp.addListener("appUrlOpen", async ({ url }) => {
      console.log("[appUrlOpen] received:", url);

      // Invite-link landing on native iOS — same shapes the web side
      // parses (?invite=CODE or /invite/CODE). Funnels straight into
      // consumeInviteCode so the family/friend invite RPC fires once,
      // regardless of whether the link came in via iMessage tap or a
      // pasted URL on web.
      if (url) {
        const inviteMatch = url.match(/[?&]invite=([A-Z0-9]{4,8})\b/i)
                         || url.match(/\/invite\/([A-Z0-9]{4,8})\b/i);
        if (inviteMatch) {
          if (inviteCallbackHolder) {
            inviteCallbackHolder(inviteMatch[1]);
          } else {
            // Signed-out / mid-onboarding: nothing is mounted to
            // consume the code yet. Stash it — AuthedApp drains the
            // stash on mount, so the invite survives the full
            // signup + onboarding round trip.
            stashInvite(inviteMatch[1]);
          }
          return;
        }
      }

      // Share-extension handoff. iOS Share Extension forwards
      // app.booda.ios://share?url=<encoded-tiktok-link> when the user
      // picks Booda from the OS share sheet. Stash the URL, hop to the
      // Create tab; CreateMenu opens AddRecipeFromSheet pre-filled in
      // LINK mode the moment the prop lands.
      if (url && url.toLowerCase().startsWith("app.booda.ios://share")) {
        const m = url.match(/[?&]url=([^&#]+)/);
        if (m) {
          const decoded = decodeURIComponent(m[1]);
          // Dispatch into AuthedApp via the module-level holder.
          // setPendingSharedRecipeUrl + setTab live in that sibling
          // component, not in MainApp's scope.
          if (typeof sharedUrlCallbackHolder === "function") {
            sharedUrlCallbackHolder(decoded);
          } else {
            // Cold launch — AuthedApp isn't mounted yet. Stash; its
            // share effect drains this on mount.
            stashedShareUrl = decoded;
          }
        }
        return;
      }

      // Incoming recipe share — universal link booda.app/r/<token>
      // (recipeShares.js SHARE_LINK_BASE). Tokens are 64 hex chars;
      // match loosely so a future token format change doesn't strand
      // links on old binaries.
      if (url) {
        const shareMatch = url.match(/\/r\/([A-Za-z0-9_-]{16,})/);
        if (shareMatch) {
          if (typeof incomingShareCallbackHolder === "function") {
            incomingShareCallbackHolder(shareMatch[1]);
          } else {
            stashedShareToken = shareMatch[1];
          }
          return;
        }
      }

      if (!url || !url.includes("auth-callback")) return;

      // Dismiss any in-app SFSafariViewController if one was opened
      // (no-op when external Safari was used instead — which is our
      // normal path). Belt-and-suspenders.
      try { await Browser.close(); } catch { /* ignore */ }

      // Extract auth params via regex first — URL constructor can
      // mishandle custom schemes on some iOS versions, parsing the
      // host as the scheme's first path segment and dropping the
      // search string. Regex sidesteps that entirely.
      const codeMatch = url.match(/[?&]code=([^&#]+)/);
      const stateMatch = url.match(/[?&]state=([^&#]+)/);
      const errorMatch = url.match(/[?&]error=([^&#]+)/);
      const errorDescMatch = url.match(/[?&]error_description=([^&#]+)/);
      const fragMatch = url.match(/#(.+)$/);

      console.log("[oauth] url shape — code:", !!codeMatch, "state:", !!stateMatch, "error:", !!errorMatch, "fragment:", !!fragMatch);

      if (errorMatch) {
        console.warn("[oauth] provider returned error:", decodeURIComponent(errorMatch[1]),
          errorDescMatch ? decodeURIComponent(errorDescMatch[1]) : "");
        return;
      }

      try {
        if (codeMatch) {
          // PKCE flow — extract just the code, pass it to Supabase.
          // We also pass the full URL as a fallback in case the
          // client expects to read state/PKCE-verifier params from it.
          const code = decodeURIComponent(codeMatch[1]);
          console.log("[oauth] attempting exchangeCodeForSession with code length:", code.length);
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          console.log("[oauth] exchangeCodeForSession →", {
            hasSession: !!data?.session,
            userId: data?.session?.user?.id,
            error: error?.message,
          });
          if (error) throw error;
        } else if (fragMatch) {
          // Implicit-flow fallback — tokens in the URL fragment.
          const params = new URLSearchParams(fragMatch[1]);
          const access_token = params.get("access_token");
          const refresh_token = params.get("refresh_token");
          if (access_token && refresh_token) {
            const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
            console.log("[oauth] setSession →", {
              hasSession: !!data?.session,
              userId: data?.session?.user?.id,
              error: error?.message,
            });
            if (error) throw error;
          } else {
            console.warn("[oauth] fragment missing tokens — keys present:", [...params.keys()]);
          }
        } else {
          console.warn("[oauth] callback URL has neither code nor fragment tokens:", url);
        }
      } catch (e) {
        console.warn("[oauth] deep-link handler threw:", e?.message || e);
      }
    }).then((handle) => { removeFn = handle.remove.bind(handle); });
    return () => {
      try { removeFn?.(); } catch { /* ignore */ }
      try { removeAppState?.(); } catch { /* ignore */ }
    };
    // Listener registers once. consumeInviteCode is accessed via the
    // ref (see comment above the useEffect) so we don't have to list
    // it as a dep — which would crash with a TDZ ReferenceError since
    // consumeInviteCode is declared further down the component body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Avatar catalog + owned pool + pin RPC. Single source of truth
  // for anything character-avatar related — Settings renders the
  // collection grid and drives pin. See migrations 0117/0118 for
  // the backend.
  const avatars = useAvatars(user?.id);

  // --kb-h is now written by useKeyboardOffset (mounted below) as an
  // alias of --kb-offset. The old standalone Capacitor listener
  // that lived here was a duplicate writer for the same variable;
  // deleted in the 2026-05-23 keyboard consolidation (commit 5a/5).


  // On first sign-in (or if an older account has no name yet), save whatever
  // the provider gave us so Home can personalise greetings. Avatars are
  // no longer sourced from the OAuth session — we use an in-game catalog
  // (migration 0117) so users feel like a character, not themselves. The
  // starter-grant RPC seeds their pool + picks an initial avatar_slug
  // the first time they land.
  useEffect(() => {
    if (!user || profileLoading) return;
    const googleName = nameFromAuth(user);
    if (!profile) {
      upsertProfile({ name: googleName }).catch(console.error);
    } else if (!profile.name && googleName) {
      upsertProfile({ name: googleName }).catch(console.error);
    }
  }, [user, profile, profileLoading, upsertProfile]);

  // Grant starter avatars as soon as the user + catalog are both
  // loaded. RPC short-circuits if they already own anything. The
  // returned row carries the active slug / URL — patch the local
  // profile so the first render after sign-in already shows a
  // character instead of the initial-letter fallback.
  //
  // ONE-SHOT GATE: granting starters is a once-per-user action and
  // grantStarters() internally calls loadOwned() which toggles
  // avatars.ready (false → true) on every call. Without this ref
  // the effect re-fired on every ready flip, spamming the network
  // with grant_starter_avatars + user_avatars SELECT pairs forever
  // (visible in Network tab as 30+ rps of identical 200s). The ref
  // guarantees one call per user; reset on user change so a sign-out
  // / sign-in cycle re-arms the grant for the next account.
  const grantedForUserIdRef = useRef(null);
  useEffect(() => {
    if (!user?.id) return;
    if (!avatars.ready) return;
    if (grantedForUserIdRef.current === user.id) return;
    grantedForUserIdRef.current = user.id;
    let cancelled = false;
    (async () => {
      const result = await avatars.grantStarters();
      if (cancelled || !result) return;
      patchProfile({ avatar_slug: result.slug, avatar_url: result.url });
    })();
    return () => { cancelled = true; };
  }, [user?.id, avatars.ready, avatars.grantStarters, patchProfile]);

  if (authLoading) return <LoadingSplash />;

  if (!user) {
    return (
      <MCMThemeProvider>
        <WarmBackdrop />
        <div style={{ ...pageShell }}>
          <SignIn />
        </div>
      </MCMThemeProvider>
    );
  }

  if (profileLoading) return <LoadingSplash />;

  // Kid Mode gate. Two paths to enter:
  //   1. Modern path (post 0224 rework): the parent's session
  //      activates a kid context via setActiveKidId. KidModeShell
  //      mounts with that kid's profile.id; parent stays signed in
  //      underneath. Exit clears the state and returns to home.
  //   2. Legacy path: a kid auth.users row that hasn't been deleted
  //      yet still signs in as themselves with profile_role='child'.
  //      Falls through to the same shell, taking their own user_id.
  //      This branch becomes dead once migration 0227 removes the
  //      remaining kid auth.users rows.
  if (activeKidId || profile?.profile_role === "child") {
    const kidId = activeKidId || user.id;
    return (
      <MCMThemeProvider>
        <ErrorBoundary
          label="kids-mode"
          onReset={activeKidId ? () => setActiveKidId(null) : null}
        >
          <KidModeShell
            kidId={kidId}
            onExit={activeKidId ? () => setActiveKidId(null) : null}
          />
        </ErrorBoundary>
      </MCMThemeProvider>
    );
  }

  // Onboarding gate: keys on onboarded_at, which ONLY the completion
  // upsert below sets. Row existence is NOT the signal — the save-
  // provider-name effect above auto-creates the row on first sign-in
  // (name: null for magic-link users), and when this gate keyed on
  // row existence that upsert unmounted Onboarding one frame after it
  // rendered. Every new user got dumped into the app with no name,
  // dietary, username, or friend-finding (migration 0281 backfilled
  // onboarded_at for genuinely-onboarded rows via username-present).
  const isNewUser = !profile || !profile.onboarded_at;
  if (isNewUser) {
    // eslint-disable-next-line no-console
    console.log("[App] showing onboarding — profile:", profile);
    return (
      <MCMThemeProvider>
        <WarmBackdrop />
        <div style={{ ...pageShell }}>
          <Onboarding
            // Prefills: Google users arrive with a provider name; husk
            // rows (created by the old gate bug) may carry values the
            // user set later via Settings — re-onboarding must show
            // them, not blank them.
            defaultName={profile?.name || nameFromAuth(user) || ""}
            existingProfile={profile || null}
            avatars={avatars}
            userId={user.id}
            onComplete={async (answers) => {
              // Post-onboarding pipeline: tour → find friends. Set
              // BEFORE any await — the upsert's setProfile schedules
              // the re-render that mounts AuthedApp, and AuthedApp's
              // initializer reads this key; a later await (dynamic
              // import chunk fetch) loses that race and the tour
              // silently never fires. localStorage (not session) so
              // an app kill between onboarding and tour doesn't
              // strand it; each stage clears itself in AuthedApp.
              try { localStorage.setItem("booda:postOnboarding", "tour"); } catch {}
              const cities = Array.isArray(answers.shoppingCities)
                ? answers.shoppingCities
                : [];
              // Hash the phone the user typed during onboarding (same
              // peppered SHA-256 as Settings → LET FRIENDS FIND ME).
              // Skipped step → answers.phone is "" → phone_hash stays
              // null, user opts in later from Settings.
              let phoneHash = null;
              if (answers.phone && answers.phone.trim()) {
                try {
                  const { hashRawPhone } = await import("./lib/phoneHash");
                  phoneHash = await hashRawPhone(answers.phone);
                } catch (e) {
                  console.warn("[onboarding] phone hash failed:", e?.message || e);
                }
              }
              const cleanedUsername = (answers.username || "").trim().toLowerCase();
              const usernameValid = /^[a-z0-9_]{3,24}$/.test(cleanedUsername);
              // Persona presets kitchen_tracking; the toggle stays
              // independently editable in Settings afterwards.
              const cookStyle = answers.cookStyle || null;
              await upsertProfile({
                ...(answers.name && answers.name.trim() ? { name: answers.name.trim() } : {}),
                // Steps hidden by the persona's step set arrive as
                // undefined — only write what was actually collected
                // so re-onboarding a husk row can't blank Settings-set
                // values.
                ...(answers.dietary !== undefined ? { dietary: answers.dietary } : {}),
                ...(answers.veganStyle !== undefined ? { vegan_style: answers.veganStyle || null } : {}),
                ...(answers.level !== undefined ? { skill_self_report: answers.level } : {}),
                ...(answers.goal !== undefined ? { goal: answers.goal } : {}),
                // Onboarding city step writes an array (may be empty
                // if user skipped). Empty arrays are fine — the user
                // can fill it later in Settings, and the brand picker
                // falls back to "us" generic until a city is set.
                ...(answers.shoppingCities !== undefined ? { shopping_cities: cities } : {}),
                ...(phoneHash ? { phone_hash: phoneHash } : {}),
                ...(usernameValid ? { username: cleanedUsername } : {}),
                ...(Array.isArray(answers.allergies) ? { allergies: answers.allergies } : {}),
                // Persona keys only when a persona was actually
                // picked — a re-onboarding SKIP must not clobber a
                // Settings-set cook_style / kitchen_tracking, and for
                // genuinely new users the DB defaults (null / true)
                // already say the same thing.
                ...(cookStyle
                  ? {
                      cook_style: cookStyle,
                      kitchen_tracking: !(cookStyle === "simple" || cookStyle === "dayof"),
                    }
                  : {}),
                // Photo beats avatar pick beats whatever grantStarters
                // chose (fallback when both were skipped). The pick
                // branch writes avatar_url too — render sites key on
                // avatar_url, so slug alone would keep showing the
                // boot-grant's random starter.
                ...(answers.photoUrl
                  ? { avatar_url: answers.photoUrl, avatar_mode: "photo", avatar_slug: null }
                  : answers.avatarSlug
                    ? {
                        avatar_slug: answers.avatarSlug,
                        avatar_mode: "pinned",
                        ...(answers.avatarUrl ? { avatar_url: answers.avatarUrl } : {}),
                      }
                    : {}),
                onboarded_at: new Date().toISOString(),
              });
              // Skip case ONLY: ensure the starter grant landed. When
              // a photo or pick was chosen, calling this would revert
              // it — grant_starter_avatars sees avatar_slug IS NULL on
              // the photo branch and stamps a starter back over the
              // uploaded photo's avatar_url.
              if (!answers.photoUrl && !answers.avatarSlug) {
                try { avatars?.grantStarters?.(); } catch { /* non-fatal */ }
              }
              // Fire-and-forget regional brand enrichment for the
              // cities the user picked during onboarding. Each
              // region only enriches once (cached via
              // regional_brand_enrichment), so the user pays no
              // visible cost — the localized brands surface in
              // their picker by next session.
              // Wrapped: finish() now surfaces onComplete rejections
              // as a retry screen, and a failed chunk fetch here must
              // not read as "your profile didn't save" — it did.
              if (cities.length > 0) {
                try {
                  const { triggerRegionEnrichment } = await import("./lib/enrichRegionBrands");
                  triggerRegionEnrichment(cities);
                } catch (e) {
                  console.warn("[onboarding] region enrichment skipped:", e?.message || e);
                }
              }
            }}
          />
        </div>
      </MCMThemeProvider>
    );
  }

  // ToastProvider wraps the authenticated tree so useToast() works inside
  // the hooks that hang off of user data (usePantry's realtime callback etc.).
  return (
    <ToastProvider>
      <IngredientInfoProvider>
        <BrandNutritionProvider>
          <AuthedApp
            user={user}
            profile={profile}
            upsertProfile={upsertProfile}
            patchProfile={patchProfile}
            avatars={avatars}
            onEnterKidMode={setActiveKidId}
          />
          {showRecoverySheet && (
            <NewPasswordSheet
              open
              recovery
              onClose={() => setShowRecoverySheet(false)}
            />
          )}
        </BrandNutritionProvider>
      </IngredientInfoProvider>
    </ToastProvider>
  );
}

// The authenticated app tree. Lives inside ToastProvider so realtime
// callbacks can raise toasts.
function AuthedApp({ user, profile, upsertProfile, patchProfile, avatars, onEnterKidMode }) {
  const [tab, setTab] = useState("home");
  // Pantry-off mode (personas "simple" / "dayof", or the Settings
  // "Track my kitchen inventory" toggle). The Kitchen tab drops out
  // of the dock, AI generation runs prompt-first without pantry
  // anchoring, and pantry-match chrome is suppressed — the feature
  // stays one Settings flip away, it just stops pushing itself.
  const kitchenTracking = profile?.kitchen_tracking !== false;
  // shopping_cities → brand-picker region sync. Lived in Settings'
  // PreferencesSection before, which only ran when Settings was
  // OPENED — onboarding-set cities left every brand picker on the
  // stale default region until then.
  useEffect(() => {
    syncRegionFromProfile(profile);
  }, [profile]);
  // Active-store truth-up at boot. Shop Mode's refreshActive is the
  // normal sync path but only runs when Shop Mode MOUNTS — a crash
  // mid-trip would leave mise:active_store boosting house brands in
  // kitchen pickers indefinitely. One single-row select: an active
  // trip restores its store, no active trip clears the orphan.
  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase
          .from("shopping_trips")
          .select("store_name")
          .eq("user_id", user.id)
          .eq("status", "active")
          .order("started_at", { ascending: false })
          .limit(1);
        if (alive) setActiveStore(data?.[0]?.store_name || null);
      } catch { /* non-fatal — next Shop Mode mount re-syncs */ }
    })();
    return () => { alive = false; };
  }, [user?.id]);
  const nav = kitchenTracking ? NAV : NAV.filter((t) => t.id !== "pantry");
  const navSplit = Math.ceil(nav.length / 2);
  const { push: pushToast } = useToast();

  // Dock indicator measurement — refs to each tab button + the dock
  // container. On every tab change we re-measure the active button's
  // bounding box (relative to the dock), and store it as targetRect.
  // The DockIndicator component animates its two blobs to that rect.
  // Resize listener keeps the indicator aligned if the viewport
  // changes mid-session (rotation, browser-window resize).
  const dockRef = useRef(null);
  const dockTabRefs = useRef({});
  const setDockTabRef = useCallback((id) => (el) => {
    if (el) dockTabRefs.current[id] = el;
    else delete dockTabRefs.current[id];
  }, []);
  const [dockTargetRect, setDockTargetRect] = useState(null);
  const measureDockTarget = useCallback(() => {
    const el = dockTabRefs.current[tab];
    if (!el) return;
    // offsetLeft/Top are relative to the offsetParent's PADDING box —
    // exactly the coordinate space that `position:absolute; inset:0`
    // aligns to inside the dock. getBoundingClientRect would return
    // border-box coordinates, off by the dock's 8px/6px padding.
    //
    // The center + button has `y: -12` (framer-motion lifts it above
    // the dock baseline). offsetTop doesn't include transforms, so
    // we adjust for the create tab specifically — the indicator
    // should follow the visual position, not the layout position.
    //
    // We also INFLATE the rect by 8px in each direction for the
    // create tab so the goo indicator forms a visible teal halo
    // around the (larger) orange + button. Without inflation the
    // 52×52 indicator hides entirely behind the 52×52 button — no
    // visible active state.
    const isCreate = tab === "create";
    const yOffset = isCreate ? -12 : 0;
    const haloPad = isCreate ? 8 : 0;
    setDockTargetRect({
      x: el.offsetLeft - haloPad,
      y: el.offsetTop + yOffset - haloPad,
      width: el.offsetWidth + haloPad * 2,
      height: el.offsetHeight + haloPad * 2,
    });
  }, [tab]);
  useLayoutEffect(() => { measureDockTarget(); }, [measureDockTarget]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.addEventListener("resize", measureDockTarget);
    return () => window.removeEventListener("resize", measureDockTarget);
  }, [measureDockTarget]);

  // Tab-change scroll reset is wired into AnimatePresence's
  // onExitComplete (search for `resetScrollAfterExit`) so the
  // scroll snap is hidden inside the fade-out → fade-in gap.
  // No visible jump; the new tab's first paint is already at
  // scrollY=0. Skip "plan" — Calendar owns its own positioning.
  const resetScrollAfterExit = useCallback(() => {
    if (typeof window === "undefined") return;
    if (tab === "plan") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [tab]);

  // What's-new notification state. Compares the bundled CURRENT_VERSION
  // against the user's locally-stored last-seen version; surfaces a
  // slim notification + (on tap) the full ReleaseNotesModal. First-paint
  // unknown-history is silent — see useWhatsNew.js for the rationale.
  const whatsNew = useWhatsNew();

  // NOTE: the ingredient_info seed + fetch used to live here as a separate
  // useEffect. It's now consolidated inside IngredientInfoProvider so the
  // seeded data is actually in React state (not just in the DB) by the
  // time IngredientCard mounts — zero delay on card opens after first
  // paint. See src/lib/useIngredientInfo.js.

  const relationships = useRelationships(user?.id);
  const { familyKey } = relationships;

  // userId → display name lookup. Still useful for in-UI attribution
  // ("+ Butter added by Alice") even though notification text is now
  // pre-formatted server-side.
  const nameFor = useMemo(() => {
    const map = new Map();
    if (user?.id) map.set(user.id, profile?.name ? `${profile.name.split(/\s+/)[0]} (you)` : "You");
    for (const row of [...relationships.family, ...relationships.friends]) {
      const first = row.other?.name?.split(/\s+/)[0];
      if (row.otherId) map.set(row.otherId, first || "Family");
    }
    return (id) => map.get(id) || "Someone";
  }, [user?.id, profile?.name, relationships.family, relationships.friends]);

  // Parallel lookup for the user's profile picture. Relationships rows
  // carry avatar_url (see useRelationships select), the viewer's own
  // row carries it via useProfile. Activity feed rows only have an
  // actorId — this closure hands them the URL without re-fetching
  // per row.
  const avatarFor = useMemo(() => {
    const map = new Map();
    if (user?.id && profile?.avatar_url) map.set(user.id, profile.avatar_url);
    for (const row of [...relationships.family, ...relationships.friends]) {
      if (row.otherId && row.other?.avatar_url) {
        map.set(row.otherId, row.other.avatar_url);
      }
    }
    return (id) => map.get(id) || null;
  }, [user?.id, profile?.avatar_url, relationships.family, relationships.friends]);

  // Pantry + shopping list still subscribe to realtime so their UI updates
  // when family edits land — but we no longer raise toasts from them. Toasts
  // are now driven by the inbound notifications row (which the DB trigger
  // produces), so all surfaces stay in sync with one another.
  const [pantry, setPantry, pantryLoading] = usePantry(user?.id, familyKey);
  // Monthly receipt spend for the hero spend chip. Cheap query
  // (scoped to the current month, user's own receipts) — refreshKey
  // stays 0 since realtime pantry/receipt changes don't need to
  // re-trigger this often.
  const { cents: spendCents } = useMonthlySpend(user?.id);
  const [shoppingList, setShoppingList]   = useShoppingList(user?.id, familyKey);

  // Persistent inbox + ephemeral toast + browser notification, all wired off
  // a single inbound stream.
  const onNewNotification = useCallback((row) => {
    pushToast(row.msg, { emoji: row.emoji || "🔔", kind: row.kind || "info" });
    // OS-level notification while the tab is hidden so users get pinged even
    // when mise isn't the active tab. Permission is requested lazily on
    // bell-click below; if it was never granted this is a silent no-op.
    if (typeof document !== "undefined" && document.hidden &&
        typeof Notification !== "undefined" && Notification.permission === "granted") {
      try { new Notification("Booda", { body: `${row.emoji || ""} ${row.msg}`.trim(), tag: row.id }); }
      catch (e) { /* some browsers throw on Service-Worker-only contexts */ }
    }
  }, [pushToast]);

  const notifications = useNotifications(user?.id, { onNew: onNewNotification });

  const [pantryView, setPantryView]       = useState("stock"); // "stock" | "shopping"
  // Pantry-off snap: with the Kitchen tab out of the dock, a stale
  // tab === "pantry" bounces home — EXCEPT the shopping list, which
  // lives under the pantry tab and stays fully reachable (the
  // "dayof" persona is literally "I shop the day I cook"; GO TO
  // SHOPPING CTAs do setPantryView("shopping") + setTab("pantry")).
  useEffect(() => {
    if (!kitchenTracking && tab === "pantry" && pantryView !== "shopping") {
      setTab("home");
    }
  }, [kitchenTracking, tab, pantryView]);
  const [settingsOpen, setSettingsOpen]   = useState(false);
  const [socialOpen, setSocialOpen]       = useState(false);
  const [notifsOpen, setNotifsOpen]       = useState(false);
  // Parent-facing kid requests inbox. Top-level sheet (instead of
  // buried under Settings → Kids Mode) so notification taps can
  // open it directly with accept/decline actions.
  const [kidInboxOpen, setKidInboxOpen]   = useState(false);
  // --- MCM pantry state ---------------------------------------------
  // `mcmOpenItem` — which pantry row is open in the shared ItemCard
  // overlay, set when the user taps a card in MCM's grid. Null
  // when nothing is open. Kept at App level so the overlay renders
  // above the MCM screen instead of inside its motion wrapper
  // (which would clip modals and duplicate portals).
  const [mcmOpenItem, setMcmOpenItem]     = useState(null);
  // Receipt history state consolidated into `historyOpen` (Phase 3
  // step 4 lift). MCM hero "view receipts" button + Kitchen's
  // classic history button + any future entry point all flip the
  // same flag.
  // MCM scan + manual-add sheet. When set, the AddDraftSheet
  // overlay opens at App level. Initial value is the seed
  // record the sheet renders against — { mode: "blank" } for
  // manual entry, { mode: "scan", ...prefilledFields } for
  // post-scan draft. Null = sheet closed.
  const [mcmAddDraft, setMcmAddDraft]     = useState(null);
  // App-level Recipe Book mount so CreateMenu can open it directly
  // (the UserProfile Recipe Book button still works independently;
  // both routes write to the same state). { initialAddOpen: true }
  // auto-opens the Add-Recipe paste sheet for the "Write your own"
  // shortcut. null = closed.
  const [recipeBookState, setRecipeBookState] = useState(null);
  // Photo-mode seed bridge: when RecipeBook's `+` opens the universal
  // AddRecipeFromSheet and the user finishes the photo flow, the
  // identified items land here, the book closes, and we tab over to
  // Create — CreateMenu picks up the seed via initialAiSeed and lands
  // on its AI sub-phase with the photo pantry pre-loaded.
  const [pendingAiPhotoSeed, setPendingAiPhotoSeed] = useState(null);
  // Reverse-engineered sketch from "finished dish" photo mode. Set
  // alongside pendingAiPhotoSeed; CreateMenu reads both and uses the
  // sketch's dishName / techniques to pre-fill the meal prompt so
  // Claude inherits what the photo showed.
  const [pendingAiPhotoSketch, setPendingAiPhotoSketch] = useState(null);
  // Share-extension handoff. When iOS forwards a TikTok / Instagram /
  // YouTube URL via app.booda.ios://share?url=…, we stash it here +
  // bounce to the Create tab; CreateMenu reads `pendingSharedRecipeUrl`
  // and auto-opens AddRecipeFromSheet pre-filled in LINK mode.
  const [pendingSharedRecipeUrl, setPendingSharedRecipeUrl] = useState(null);
  // Incoming recipe-share token (booda.app/r/<token>) — mounts
  // IncomingShareSheet for preview + accept-into-own-book.
  const [incomingShareToken, setIncomingShareToken] = useState(null);
  // Direct-share row id — set when the recipient taps a recipe_share
  // notification; mounts the same IncomingShareSheet via id lookup.
  const [incomingShareId, setIncomingShareId] = useState(null);
  // Post-onboarding pipeline sequencer: "tour" → "findFriends" →
  // done. Onboarding's completion callback writes the stage to
  // localStorage (NOT sessionStorage — an app kill between onboarding
  // and the tour must not strand the pipeline); each stage advances
  // or clears the key so nothing replays on later launches.
  const [postOnboarding, setPostOnboarding] = useState(() => {
    try {
      const v = localStorage.getItem("booda:postOnboarding");
      return v === "tour" || v === "findFriends" ? v : null;
    } catch { return null; }
  });
  const advancePostOnboarding = (stage) => {
    setPostOnboarding(stage);
    try {
      if (stage) localStorage.setItem("booda:postOnboarding", stage);
      else localStorage.removeItem("booda:postOnboarding");
    } catch { /* noop */ }
  };
  const autoFindFriends = postOnboarding === "findFriends";
  const setAutoFindFriends = (open) => {
    if (!open) advancePostOnboarding(null);
  };
  // App-level SchedulePicker — fires when the SCHEDULE button is
  // tapped from the App's RecipeBook mount (the one CreateMenu opens
  // for "Pick a recipe"). UserProfile mounts its own picker for the
  // avatar-menu Recipe Book; these are independent flows.
  const [appSchedulingRecipe, setAppSchedulingRecipe] = useState(null);
  // Full-screen success image shown after AddDraftSheet commits.
  // { mode: "manual"|"scanner", row } — null when hidden.
  const [addSuccess, setAddSuccess]       = useState(null);
  // forceClassicPantry escape hatch removed (Phase 3 final step) —
  // the legacy Kitchen render was the only consumer, and ShopMode
  // / ShopModeCheckout / Scanner / ReceiptView are all mounted at
  // App level now.
  // Open state for the MCM shopping screen's quick-add modal —
  // MCMShoppingAdd, a warm-palette bottom-sheet with name + qty
  // (full canonical lock-in lives in AddDraftSheet for the
  // kitchen-stocking path; this is the brain-empty trip jot path).
  const [mcmShoppingAddOpen, setMcmShoppingAddOpen] = useState(false);
  const isAdmin = profile?.role === "admin";
  // Create menu overlay. Opened by the floating ➕ in the tab bar;
  // renders on top of whatever tab is currently active. Hosts every
  // creation flow: cooking (custom / AI / template recipe) AND
  // pantry ingress (scan / manual add).
  // CreateMenu is now a real tab destination (`tab === "create"`)
  // instead of a fixed-position overlay. prevTab caches what tab
  // the user came from so Close returns them there.
  const [prevTab, setPrevTab] = useState("home");
  // App-level ShoppingQuickAdd mount so CreateMenu can open the
  // sheet from anywhere. Kitchen still owns its own copy when the
  // user adds via the shopping list page directly.
  const [shoppingQuickAddOpen, setShoppingQuickAddOpen] = useState(false);
  // EatQuickSheet — pantry-less "I ate this" path. CreateMenu's Eat
  // card flips this; the sheet writes a consumption_logs row with
  // pantry_row_id: null and optionally appends to shopping list.
  const [eatQuickOpen, setEatQuickOpen] = useState(false);
  // Shop Mode mounts at App level so it's reachable from Home, the
  // CreateMenu, etc. without going through Kitchen. The new ShopMode
  // owns the full three-phase flow internally (scan/resolve/checkout),
  // so there's no separate pendingTripCtx handoff anymore.
  const [shopModeOpen, setShopModeOpen]     = useState(false);
  // Phase 3 step 4 — Receipt deep-link + history mount at App level.
  // Kitchen still triggers via callback props for its internal
  // surfaces (provenance taps in classic ItemCard, history button
  // in classic shopping fallback).
  const [openReceiptId, setOpenReceiptId]   = useState(null); // { receiptId } | { scanId } | null
  const [historyOpen, setHistoryOpen]       = useState(false);
  // Trip history modal — sibling of receipt history, scoped to the
  // shopping_trips table so trips committed without a receipt are
  // reachable for after-the-fact provenance attach.
  const [tripsHistoryOpen, setTripsHistoryOpen] = useState(false);
  const [openTripId, setOpenTripId] = useState(null);
  const [tripsRefreshKey, setTripsRefreshKey] = useState(0);
  // Expose dev-mode reset on window so devtools can wipe learned state
  // (caches + user-scoped scan corrections) for fresh-scan testing.
  // `await window.miseReset()` from the console rebuilds the app from
  // scratch with this user's id baked in.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    import("./lib/devReset").then(({ resetLearnedState }) => {
      if (cancelled) return;
      window.miseReset = (opts = {}) => resetLearnedState({ userId: user?.id, ...opts });
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Surfaced when useSyncedList reports a failed insert/update/delete.
  // Without this banner the user only sees "I added it and it's gone"
  // with no explanation; the actual Supabase error is buried in the
  // browser console where they aren't looking.
  const [dbWriteError, setDbWriteError] = useState(null);
  useEffect(() => {
    const onFail = (e) => {
      const d = e.detail || {};
      setDbWriteError({
        table: d.table || "?",
        op: d.op || "?",
        error: d.error || "unknown error",
        when: Date.now(),
      });
    };
    window.addEventListener("mise:dbWriteFailed", onFail);
    return () => window.removeEventListener("mise:dbWriteFailed", onFail);
  }, []);
  // Pantry-action intent dispatched by CreateMenu's ADD TO PANTRY
  // branch. Kitchen consumes this in an effect and flips its
  // already-mounted Scanner / AddItemModal into the open state that
  // matches. 'scan' | 'add' | 'shopMode' | null.
  const [pendingPantryAction, setPendingPantryAction] = useState(null);

  // Consume pendingPantryAction === 'shopMode' at App level (was in
  // Kitchen.jsx pre-Phase-3). Lifts the trigger out of the classic
  // orchestrator so Shop Mode is reachable without depending on
  // Kitchen's mount.
  useEffect(() => {
    if (pendingPantryAction === "shopMode") {
      setShopModeOpen(true);
      setPendingPantryAction(null);
    }
  }, [pendingPantryAction]);

  // ── SFX wiring ──────────────────────────────────────────────────
  // Preload all five sound effects on mount. Decoded buffers stay in
  // memory; subsequent playSfx() calls fire instantly via Web Audio
  // BufferSource. Browsers block AudioContext until first gesture —
  // playSfx handles the resume() implicitly when triggered from a
  // gesture handler.
  useEffect(() => { preloadSfx(); }, []);

  // Open-transition watchers. Each fires its respective SFX exactly
  // when the surface transitions from closed → open, regardless of
  // which call site triggered the state change. Closed → closed
  // (re-render with null) and open → open (e.g. swapping items
  // without intermediate close) don't refire — the user only wants
  // the sound on the actual "this just opened" moment.
  const prevMcmOpenRef = useRef(null);
  useEffect(() => {
    const wasOpen = !!prevMcmOpenRef.current;
    if (!wasOpen && mcmOpenItem) playSfx("open-drill-down");
    prevMcmOpenRef.current = mcmOpenItem;
  }, [mcmOpenItem]);

  const prevReceiptRef = useRef(null);
  useEffect(() => {
    const wasOpen = !!prevReceiptRef.current;
    if (!wasOpen && openReceiptId) playSfx("receipt-open");
    prevReceiptRef.current = openReceiptId;
  }, [openReceiptId]);

  // historyOpen — the receipts LIST modal (kitchen tab's top-right
  // Receipt button, ShopMode hero "view receipts", etc). Same SFX
  // as opening a single receipt.
  const prevHistoryRef = useRef(false);
  useEffect(() => {
    if (historyOpen && !prevHistoryRef.current) playSfx("receipt-open");
    prevHistoryRef.current = historyOpen;
  }, [historyOpen]);

  const prevPantryViewRef = useRef(pantryView);
  useEffect(() => {
    if (pantryView === "shopping" && prevPantryViewRef.current !== "shopping") {
      playSfx("shopping-cart-open");
    }
    prevPantryViewRef.current = pantryView;
  }, [pantryView]);

  const prevRecipeBookRef = useRef(null);
  useEffect(() => {
    const wasOpen = !!prevRecipeBookRef.current;
    if (!wasOpen && recipeBookState) playSfx("paper-flipping");
    prevRecipeBookRef.current = recipeBookState;
  }, [recipeBookState]);

  // Admin-panel open state. Only reachable via Settings → ADMIN entry,
  // which is itself gated on profile.role === 'admin' (0042).
  const [adminOpen, setAdminOpen]         = useState(false);
  // deepLink is set when a notification tap wants to navigate. Cookbook
  // consumes it on mount and calls setDeepLink(null) to clear.
  // Shape: { kind: 'cook_log', id: '<uuid>' } | null
  const [deepLink, setDeepLink]           = useState(null);

  // Whose profile is open, if any. A string user id; null = closed.
  // Lives at App level so every surface (Settings, Cookbook, etc) can
  // route into the same overlay.
  const [profileUserId, setProfileUserId] = useState(null);

  // ── Cook mode (lifted from CreateMenu / Plan) ────────────────────
  // CookMode is mounted ONCE at the App level so it survives tab
  // changes and overlay dismissals. useActiveCookSession tails the
  // cook_sessions table; when a session is open (status='active',
  // within the 2h resume window), CookBanner appears pinned at the
  // top of the app with the live step + timer countdown. Tapping the
  // banner re-opens CookMode at the step the user was on.
  //
  // Why ONE mount, not per-tab: before, CreateMenu and Plan each
  // rendered their own CookMode. Unmounting either (closing the
  // overlay, navigating tabs) destroyed the cook view and made "close
  // the app then come back" a broken experience. One mount + a banner
  // for resume + no auto-abandon-on-unmount inside CookMode = the
  // cook survives everything short of the 2h window elapsing or the
  // user explicitly finalizing from the DONE LOG IT button.
  const [cookModeRecipe, setCookModeRecipe]     = useState(null);
  const [cookModeView, setCookModeView]         = useState("overview");
  const [cookModeStepIdx, setCookModeStepIdx]   = useState(0);
  const [cookModeEndsAt, setCookModeEndsAt]     = useState(null);
  // Stage index parsed from telemetry step_id's `-s${stageIdx}` suffix
  // (TimerStack stages register with that format). null when the
  // active step is a single-Timer step or there's no active step.
  const [cookModeStageIdx, setCookModeStageIdx] = useState(null);
  // Lapsed-cook closure flags — set by the LapsedCookBanner's
  // FINISH & LOG path: adopt this exact session id (any age) and
  // skip the step list straight into the post-cook logging flow.
  const [cookModeResumeSessionId, setCookModeResumeSessionId] = useState(null);
  const [cookModeFinishNow, setCookModeFinishNow] = useState(false);
  const [cookModeJoinSessionId, setCookModeJoinSessionId] = useState(null);
  // Banner-as-switcher: the user's tap on a cook tab (or horizontal
  // swipe) writes here; useActiveCookSession then resolves the
  // focused cook by id. Null = banner shows most-recent active cook
  // (single-cook fallback behavior).
  const [focusedCookSessionId, setFocusedCookSessionId] = useState(null);
  const activeCook = useActiveCookSession(user?.id, focusedCookSessionId);
  // Multi-cook awareness: every active cook the user can see (own,
  // family, team-cook participations). Drives the "ALSO COOKING" strip
  // below the singular CookBanner so a parallel-cooking scenario
  // (Trevor's steak + Marissa's broccoli + family member's dessert)
  // surfaces all three on every device with tap-to-switch.
  const { cooks: allActiveCooks } = useActiveCookSessions(user?.id);
  // Meal-plan data hook stays mounted so the data layer is exercised
  // and the migration's column / trigger keeps being reachable. UI
  // surfaces (builder + planned-meal cards) were descoped after the
  // user's feedback on the conductor view; will re-introduce once
  // banner-as-switcher proves itself.
  const {
    saveRecipe: saveUserRecipe,
    findBySlug: findUserRecipeBySlug,
    recipes: userRecipeRows,
    updateRecipeContent: updateUserRecipeContent,
  } = useUserRecipes(user?.id);
  // Post-cook revise seed — set by CookComplete's "REVISE FOR NEXT
  // TIME" offer ({ recipe, seedText }). Mounts the App-level
  // RecipeReviseSheet below; the paid AI call still fires only on
  // the sheet's explicit button.
  const [reviseSeed, setReviseSeed] = useState(null);
  // Cross-device mirror: every cook_session this user is a participant
  // on (helper or diner — not the lead) spawns a local Live Activity
  // driven by Supabase realtime. So when Dad taps START on dinner, the
  // rest of the family's phones light up with the cook's progress
  // automatically. No-op on web; lead's CookMode runs the "real" one.
  useTeamLiveActivity(user?.id, { findUserRecipe: findUserRecipeBySlug });
  // APNs push-token capture for Path A — feeds the cook-activity-push
  // edge function with both per-activity tokens (state UPDATES while
  // an activity is running) and per-user push-to-start tokens
  // (SPAWN an activity on a participant's lock screen the moment a
  // cook session inserts, no app open required). iOS-only; no-op
  // elsewhere.
  useActivityPushTokens(user?.id);

  // Global iOS keyboard offset: sets --kb-offset CSS variable on
  // the document root so any sticky-bottom bar or search input
  // can stay above the keyboard. See src/lib/useKeyboardOffset.js
  // for the full pattern + usage.
  useKeyboardOffset();
  // Scheduling writer for the App-level SchedulePicker. Family +
  // friend profiles can't schedule on someone else's calendar, but
  // the App-level RecipeBook always belongs to the signed-in user.
  const { schedule: scheduleAppMeal } = useScheduledMeals(user?.id);

  // Open CookMode on a recipe. Default flow: land on overview
  // (ingredient checklist + swap UI) and let the user tap "Start
  // cooking" to enter the step-by-step view. When there's an ACTIVE
  // cook_session for this recipe (push deep-link, tap on the pinned
  // banner, a fresh Cook Now on a recipe you walked away from 10 min
  // ago), jump straight to the live step instead — nobody wants to
  // re-approve ingredients mid-braise. Explicit opts override.
  const openCookMode = useCallback((recipe, opts = {}) => {
    // eslint-disable-next-line no-console
    console.log("[App] openCookMode", { hasRecipe: !!recipe, title: recipe?.title, opts });
    if (!recipe) return;
    const activeSameRecipe = activeCook.session && activeCook.session.recipe_slug === recipe.slug;
    // Telemetry's step_id may carry a `-s${stageIdx}` suffix when the
    // active row was a TimerStack stage (each stage registers
    // independently to drive its own push deadline). Strip the
    // suffix before matching against recipe.steps[].id, otherwise
    // findIndex returns -1 and the resume falls back to step 0.
    const baseStepId = (id) => String(id || "").replace(/-s\d+$/, "");
    const liveStepIdx = activeSameRecipe && activeCook.activeStep && recipe.steps
      ? recipe.steps.findIndex(s => String(s.id) === baseStepId(activeCook.activeStep.step_id))
      : -1;
    setCookModeRecipe(recipe);
    // Team-cook join: bypass overview entirely. The helper is
    // joining an in-progress cook, not previewing the recipe.
    const joiningTeamCook = !!opts.joinSessionId;
    // finishNow MUST land on the cook view — the post-cook screen
    // (`{completing && <CookComplete>}`) only mounts inside the cook
    // branch, so a finishNow open that fell to "overview" would set
    // completing=true with nothing to render it.
    setCookModeView(opts.view || (opts.finishNow || joiningTeamCook || activeSameRecipe ? "cook" : "overview"));
    setCookModeStepIdx(
      Number.isFinite(opts.stepIndex)
        ? opts.stepIndex
        : liveStepIdx >= 0 ? liveStepIdx : 0,
    );
    // Carry the server-side deadline through so the in-app Timer
    // calibrates to "the push is 30s away" instead of starting a
    // fresh mm:ss. Null on fresh cooks.
    setCookModeEndsAt(activeSameRecipe ? activeCook.timerEndsAt : null);
    // Parse the TimerStack stage suffix off the active step_id so
    // CookMode can hand it to TimerStack for resume hydration. A
    // mismatched-recipe activeStep is irrelevant here — only thread
    // the index when we're resuming the same cook.
    const stageMatch = activeSameRecipe
      ? /-s(\d+)$/.exec(String(activeCook.activeStep?.step_id || ""))
      : null;
    setCookModeStageIdx(stageMatch ? Number(stageMatch[1]) : null);
    // Lapsed-cook closure: adopt this exact session (any age) and,
    // when finishNow rides along, jump straight to the post-cook
    // logging flow. Reset on every open so normal cooks don't
    // inherit stale flags.
    setCookModeResumeSessionId(opts.resumeSessionId || null);
    setCookModeFinishNow(!!opts.finishNow);
    // Team-cook join: deep-link from a participant push routes here
    // with a specific session ID. CookMode passes this through to
    // useCookTelemetry, which adopts the session instead of creating
    // a duplicate for the helper.
    setCookModeJoinSessionId(opts.joinSessionId || null);
  }, [activeCook.session, activeCook.activeStep, activeCook.timerEndsAt]);

  // Background recipe generation. The final pass is the slow Sonnet
  // call with no server-side fallback — when the upstream stalls, the
  // foreground flow has no timeout and the user is pinned to a spinner
  // forever. Instead AIRecipe hands the job here: we mint a pending
  // Recipe-Book stub, drop the user onto it, and run generation
  // detached (owned by App, so it survives the CreateMenu overlay
  // closing). The stub flips to the finished recipe — or a
  // "failed → Retry" state — when generation resolves.
  const startBackgroundRecipeGen = useCallback(async ({
    payload, finalizeInputs, titleGuess, subtitleGuess, emojiGuess,
  }) => {
    const stubMeta = {
      title: titleGuess || "New recipe",
      subtitle: subtitleGuess || "",
      emoji: emojiGuess || "🍳",
    };
    let row = null;
    try {
      // Optimistic INSERT via the user-recipes hook so the row mirrors
      // into the book instantly + gets a collision-safe slug.
      row = await saveUserRecipe(
        { ...stubMeta, ingredients: [], steps: [], _gen: "pending", _genStartedAt: new Date().toISOString() },
        "ai",
      );
    } catch (e) {
      console.error("[App] background stub save failed:", e);
      pushToast("Couldn't start that recipe — try again", { emoji: "⚠️", kind: "warn" });
      return;
    }
    if (!row?.id) {
      pushToast("Couldn't start that recipe — try again", { emoji: "⚠️", kind: "warn" });
      return;
    }
    // Leave the Create tab and open the Recipe Book deep-linked to the
    // pending stub so the user watches it fill in.
    setTab(prevTab);
    setRecipeBookState({ initialAddOpen: false, initialSlug: row.slug });
    pushToast("Cooking up your recipe — it'll appear in your book", { emoji: "📖", kind: "info" });
    // Fire-and-forget: nothing awaits the generation; status lands in
    // the row via raw updates that realtime carries into the open book.
    runBackgroundRecipeGen({ stubId: row.id, payload, finalizeInputs, stubMeta });
  }, [saveUserRecipe, prevTab, pushToast]);

  // Retry a failed/stuck background generation from the Recipe Book.
  // Reads the inputs stashed at first-run time (localStorage); when
  // they're gone (different device / cleared storage) we send the user
  // back to Create to regenerate from scratch.
  // One-time cleanup: strip bogus leftover/reheat blocks the old
  // chained-enrichment bug stapled onto served-fresh dishes (steak,
  // seared fish). Runs once per user; conservative (never touches batch
  // components or reheatable proteins). Gated by a localStorage flag so
  // it costs one scan + a couple writes, once, ever.
  useEffect(() => {
    if (!user?.id || !Array.isArray(userRecipeRows) || userRecipeRows.length === 0) return;
    const flag = `reheatFreshCleanup:v1:${user.id}`;
    try { if (localStorage.getItem(flag)) return; } catch (_) { /* private mode */ }
    const offenders = userRecipeRows.filter((r) => {
      const rec = r?.recipe;
      return rec && rec.reheat && stripBogusReheat(rec) !== rec;
    });
    try { localStorage.setItem(flag, "1"); } catch (_) { /* noop */ }
    offenders.forEach((r) => {
      updateUserRecipeContent(r.id, stripBogusReheat(r.recipe)).catch(() => {});
    });
  }, [user?.id, userRecipeRows, updateUserRecipeContent]);

  const retryBackgroundGen = useCallback(async (stubId) => {
    const restarted = await retryBackgroundRecipeGen(stubId);
    if (!restarted) {
      setRecipeBookState(null);
      setPrevTab(tab);
      setTab("create");
      pushToast("Couldn't restore that draft — start a new recipe", { emoji: "🔁", kind: "info" });
    }
  }, [tab, pushToast]);

  // Close CookMode without killing the session. The session row stays
  // active in cook_sessions; CookBanner surfaces it. Explicit abandon
  // only happens when the user finalizes (DONE LOG IT → endCook with
  // status='finished') or when the 2h window lapses.
  const closeCookMode = useCallback(() => {
    console.log("[App] closeCookMode FIRED", {
      had_cookModeRecipe: !!cookModeRecipe,
      slug: cookModeRecipe?.slug,
    });
    setCookModeRecipe(null);
    // activeCook will refresh via realtime; force one in case the
    // realtime channel was mid-reconnect.
    activeCook.refresh();
  }, [activeCook, cookModeRecipe]);

  // Explicit-abandon path. CookMode's EXIT button (not the back-arrow
  // minimize) flips the DB row to status='abandoned' and then calls
  // this with the SPECIFIC session id that was abandoned. We then
  // close the cook UI, clear the App-level session state (synchronous
  // so the banner doesn't lag), and defensively re-confirm the
  // status='abandoned' update for just that one session id (telemetry.
  // endCook may have hit a race; this is belt+suspenders).
  //
  // Pre-multi-cook era this swept ALL the user's active sessions on
  // the theory that any "active" row when EXIT was tapped was a ghost.
  // Multi-cook breaks that — Trevor's steak shouldn't get nuked just
  // because Marissa wanted to abandon her broccoli. Scope to the one
  // session id the abandoned CookMode reported.
  const abandonCook = useCallback(async (abandonedSessionId = null) => {
    // eslint-disable-next-line no-console
    console.log("[abandonCook] fired", { userId: user?.id, abandonedSessionId });
    setCookModeRecipe(null);
    activeCook.clearLocal?.();
    if (user?.id && abandonedSessionId) {
      try {
        const { data, error } = await supabase
          .from("cook_sessions")
          .update({ status: "abandoned", ended_at: new Date().toISOString() })
          .eq("id", abandonedSessionId)
          .eq("user_id", user.id)
          .eq("status", "active")
          .select("id, status");
        // eslint-disable-next-line no-console
        console.log("[abandonCook] update result:", { error: error?.message, updatedCount: data?.length, data });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[abandonCook] update threw:", e?.message || e);
      }
    }
    activeCook.refresh();
  }, [activeCook, user?.id]);

  // Resume a cook from the CookBanner tap. Three-tier resolve:
  //   1. recipe arg passed by banner (already resolved by useActiveCookSession)
  //   2. local findRecipe cascade (bundled → useUserRecipes by slug)
  //   3. direct supabase user_recipes query by slug
  //
  // Tier 3 covers the case where useUserRecipes hasn't completed
  // its initial fetch yet, OR the row was created in another tab
  // and realtime hasn't propagated, OR the cached user_recipes
  // index is stale. The async tier means the tap might not open
  // CookMode synchronously, but it will land within ~200ms instead
  // of being a no-op.
  const resumeCookFromBanner = useCallback(async (recipe, session) => {
    let r = recipe;
    let resolvedVia = recipe ? "passed-in" : null;
    if (!r && session?.recipe_slug) {
      r = findRecipe(session.recipe_slug, findUserRecipeBySlug) || null;
      resolvedVia = r ? "fallback-findRecipe" : null;
    }
    // Tier 3 — direct supabase fetch when local lookups failed. Use
    // order+limit(1) instead of maybeSingle(): maybeSingle ERRORS when
    // two rows share the slug (it happens — a re-save or a background
    // stub + foreground save), and that error was being swallowed as
    // "empty," leaving the cook unresumable. Take the most-recent row.
    if (!r && session?.recipe_slug && user?.id) {
      try {
        const { data, error } = await supabase
          .from("user_recipes")
          .select("recipe, updated_at")
          .eq("user_id", user.id)
          .eq("slug", session.recipe_slug)
          .order("updated_at", { ascending: false })
          .limit(1);
        if (!error && data?.[0]?.recipe) {
          r = data[0].recipe;
          resolvedVia = "fallback-supabase";
        } else {
          resolvedVia = "fallback-supabase-empty";
        }
      } catch (e) {
        resolvedVia = "fallback-supabase-threw";
        console.warn("[App] resumeCookFromBanner supabase fallback threw:", e);
      }
    }
    // Tier 3.5 — slug missed; match by the session's recipe TITLE. Slugs
    // drift (a re-save suffixes "-2", a background stub races a foreground
    // save), but the title is stable. This recovers the FULL recipe with
    // real step prose instead of the prose-less snapshot below.
    if (!r && session?.recipe_title && user?.id) {
      try {
        const { data } = await supabase
          .from("user_recipes")
          .select("recipe, updated_at")
          .eq("user_id", user.id)
          .eq("recipe->>title", session.recipe_title)
          .order("updated_at", { ascending: false })
          .limit(1);
        if (data?.[0]?.recipe) {
          r = data[0].recipe;
          resolvedVia = "fallback-by-title";
        }
      } catch (e) {
        console.warn("[App] resumeCookFromBanner title fallback threw:", e);
      }
    }
    // Tier 4 — LAST-RESORT reconstruction from the session's own step
    // snapshot. cook_sessions.step_summary is written at cook-start
    // exactly so a cook survives a missing/renamed recipe row. The
    // snapshot carries id + title + timer (+ instruction on cooks started
    // after this fix) — enough to resume rather than dead-end.
    if (!r && Array.isArray(session?.step_summary) && session.step_summary.length > 0) {
      r = {
        slug: session.recipe_slug,
        title: session.recipe_title || "Your cook",
        emoji: session.recipe_emoji || "🍳",
        serves: session.servings || 1,
        steps: session.step_summary.map((s) => ({
          id: s?.id,
          title: s?.title || "",
          instruction: s?.instruction || s?.title || "",
          timer: Number(s?.timer) || 0,
          ...(Array.isArray(s?.timers) && s.timers.length ? { timers: s.timers } : {}),
        })),
      };
      resolvedVia = "reconstructed-from-session";
    }
    // Diagnostic — remove once confirmed reliable on the user's device.
    // eslint-disable-next-line no-console
    console.log("[App] resumeCookFromBanner", {
      resolvedVia,
      recipeTitle: r?.title || null,
      stepCount: r?.steps?.length || 0,
      activeStepId: activeCook.activeStep?.step_id || null,
      sessionSlug: session?.recipe_slug || null,
    });
    if (!r) return;
    // Strip the `-s${stageIdx}` suffix telemetry uses for TimerStack
    // stages so the parent step still resolves cleanly on resume.
    const baseStepId = (id) => String(id || "").replace(/-s\d+$/, "");
    let idx = activeCook.activeStep && r.steps
      ? Math.max(0, r.steps.findIndex(s => String(s.id) === baseStepId(activeCook.activeStep.step_id)))
      : 0;
    // Overnight resume — when the user capped Day 1 (the overnight
    // step has finished_at stamped), there's no active step to anchor
    // on, so the legacy fallback seeds idx=0 (= "go knead again at
    // 8 AM"). Find the most-recent finished step for this session and
    // seed past it. This is also the right general behavior for any
    // resume after a step was finished but no next step started yet.
    if (!activeCook.activeStep && session?.id && r.steps?.length) {
      try {
        const { data: lastFinished } = await supabase
          .from("cook_session_steps")
          .select("step_id, finished_at")
          .eq("cook_session_id", session.id)
          .not("finished_at", "is", null)
          .order("finished_at", { ascending: false })
          .limit(1);
        const lastStepId = lastFinished?.[0]?.step_id;
        if (lastStepId) {
          const lastIdx = r.steps.findIndex(s => String(s.id) === String(lastStepId));
          if (lastIdx >= 0 && lastIdx + 1 < r.steps.length) {
            idx = lastIdx + 1;
          }
        }
      } catch (e) {
        console.warn("[App] resume past-finished lookup threw:", e);
      }
    }
    openCookMode(r, { view: "cook", stepIndex: idx });
  }, [activeCook.activeStep, openCookMode, findUserRecipeBySlug, user?.id]);

  // Lapsed-cook FINISH & LOG — the know-it-by-heart path. Adopt the
  // exact (any-age) session and jump straight into the post-cook
  // logging flow; no step list. Recipe resolution mirrors
  // resumeCookFromBanner's tiers (lapsed entries usually carry the
  // recipe already, resolved sync in useActiveCookSession).
  const finishLapsedCook = useCallback(async (recipe, session) => {
    if (!session?.id) return;
    let r = recipe || findRecipe(session.recipe_slug, findUserRecipeBySlug) || null;
    if (!r && session.recipe_slug) {
      try {
        const { data } = await supabase
          .from("user_recipes")
          .select("recipe, slug")
          .eq("user_id", user.id)
          .eq("slug", session.recipe_slug)
          .maybeSingle();
        if (data?.recipe) r = { ...data.recipe, slug: data.slug };
      } catch (e) {
        console.warn("[App] finishLapsedCook recipe fallback threw:", e);
      }
    }
    if (!r) return;
    openCookMode(r, { view: "cook", stepIndex: 0, resumeSessionId: session.id, finishNow: true });
  }, [openCookMode, findUserRecipeBySlug, user?.id]);

  // Classify a user id relative to the viewer so the UserProfile overlay
  // shows the right chip and doesn't tease cook history it won't have.
  const relationshipFor = useCallback((id) => {
    if (!id || id === user.id) return "self";
    if (relationships.family.some(r => r.otherId === id))  return "family";
    if (relationships.friends.some(r => r.otherId === id)) return "friend";
    return "stranger";
  }, [user.id, relationships.family, relationships.friends]);

  const openProfile = useCallback((id) => {
    if (!id) return;
    // Close other overlays so the user doesn't stack screens.
    setSettingsOpen(false);
    setNotifsOpen(false);
    setProfileUserId(id);
  }, []);

  // Same deep-link path the UserProfile's CookRow uses — lets the Home
  // feed hand a cook_log id and surface the meal's detail. Cookbook is
  // no longer a top-level tab (v0.11.0 nav restructure); the full
  // archive now lives inside UserProfile behind "VIEW FULL COOKBOOK",
  // so we open the viewer's own profile with the deep-link attached
  // and let UserProfile's Cookbook overlay consume it.
  const openCook = useCallback((cookLogId) => {
    if (!cookLogId) return;
    setDeepLink({ kind: "cook_log", id: cookLogId });
    setProfileUserId(user.id);
  }, [user?.id]);

  // Stabilized list of family otherIds for the activity feed cohort.
  const familyIds = useMemo(
    () => relationships.family.map(r => r.otherId).filter(Boolean),
    [relationships.family]
  );

  // Receipt deep-link consumer (Phase 3 step 4 lift). When a push
  // notification opens the app with a `deepLink.kind === "receipt"`
  // or `"pantry_scan"`, validate ownership against the pantry
  // (the artifact must point at a row the viewer or family owns)
  // and open the receipt view at App level. Stale links from
  // ex-family / wrong-account scenarios silently consume.
  // Placed after `familyIds` (and after the earlier `pantry` /
  // `deepLink` declarations) so its dependency array doesn't hit
  // a temporal-dead-zone reference at render.
  useEffect(() => {
    if (!deepLink) return;
    const canOpenProv = (ownerId) =>
      !ownerId || ownerId === user.id || familyIds.includes(ownerId);
    if (deepLink.kind === "receipt" && deepLink.id) {
      const ownerHit = pantry.find((p) => p.sourceReceiptId === deepLink.id);
      if (ownerHit && canOpenProv(ownerHit.ownerId)) {
        setOpenReceiptId({ receiptId: deepLink.id });
      }
      setDeepLink(null);
    } else if (deepLink.kind === "pantry_scan" && deepLink.id) {
      const ownerHit = pantry.find((p) => p.sourceScanId === deepLink.id);
      if (ownerHit && canOpenProv(ownerHit.ownerId)) {
        setOpenReceiptId({ scanId: deepLink.id });
      }
      setDeepLink(null);
    }
  }, [deepLink, pantry, user.id, familyIds]);

  // Route a notification tap. Currently supported:
  //   * 'cook_log'     — lands on Cookbook → that meal's detail,
  //                      composer open if the viewer was a diner.
  //   * 'user_profile' — opens the target user's profile overlay (used
  //                      by badge earn/fan-out notifications so a tap
  //                      lands on the badge wall).
  //   * 'receipt'      — lands on Kitchen tab and opens ReceiptView on
  //                      the receipt row (photo, total, item list).
  //                      Populated by migration 0040.
  //   * 'pantry_scan'  — same as receipt but for a fridge/pantry/
  //                      freezer scan artifact (pantry_scans.id).
  // Unknown kinds fall through silently so future server-side targets
  // ship without breaking old clients.
  const openNotificationTarget = useCallback((targetKind, targetId) => {
    if (!targetId) return;
    if (targetKind === "cook_log") {
      // Cookbook tab was removed in v0.11.0. Route through the viewer's
      // own UserProfile → Cookbook overlay, which consumes the deep-link
      // exactly the same way the tab did.
      setDeepLink({ kind: targetKind, id: targetId });
      setNotifsOpen(false);
      setProfileUserId(user.id);
      return;
    }
    if (targetKind === "user_profile") {
      setNotifsOpen(false);
      setProfileUserId(targetId);
      return;
    }
    // Someone sent this user a recipe copy (recipeShares.js direct
    // share). targetId is the recipe_shares row id — pop the same
    // accept/decline card the link path uses.
    if (targetKind === "recipe_share") {
      setNotifsOpen(false);
      setIncomingShareId(targetId);
      return;
    }
    if (targetKind === "receipt" || targetKind === "pantry_scan") {
      setDeepLink({ kind: targetKind, id: targetId });
      setPantryView("stock");
      setTab("pantry");
      setNotifsOpen(false);
      return;
    }
    // Prep reminders (migration 0134) stamp scheduled_meal / the
    // cook-timer pushes (migration 0137) stamp cook_session. Both
    // route to the Plan tab; Plan consumes deepLink and opens the
    // matching meal drawer (for scheduled_meal) or auto-opens
    // CookMode against the live session's recipe (for cook_session).
    if (targetKind === "scheduled_meal" || targetKind === "cook_session") {
      setDeepLink({ kind: targetKind, id: targetId });
      setTab("plan");
      setNotifsOpen(false);
      return;
    }
    // Team-cook assignment notifications (0173):
    //   step_assignment_pending — targetId is the cook_step_assignments.id;
    //     Plan tab pops the AcceptDeclineModal against the row.
    //   step_accepted / step_declined / step_completed_by_helper /
    //   step_completed_by_other — targetId is the cook_sessions.id;
    //     route to Plan so CookMode opens against that session.
    if (targetKind === "step_assignment_pending") {
      setDeepLink({ kind: targetKind, id: targetId });
      setTab("plan");
      setNotifsOpen(false);
      return;
    }
    if (
      targetKind === "step_accepted"
      || targetKind === "step_declined"
      || targetKind === "step_completed_by_helper"
      || targetKind === "step_completed_by_other"
    ) {
      setDeepLink({ kind: "cook_session", id: targetId });
      setTab("plan");
      setNotifsOpen(false);
      return;
    }
    // Kid food/meal request notifications (migration 0183 trigger).
    // target_id is the kid_food_requests row id. For recipe-kind
    // requests we look up the recipe_slug and deep-link the
    // RecipeBook to that recipe; for pantry-kind requests we just
    // open the Kids → Requests inbox (no dedicated kitchen-item
    // deep link exists yet). The trigger writes target_kind=
    // 'kid_meal_request' for recipes and 'kid_food_request' for
    // pantry items (per migration 0187 convention).
    if (targetKind === "kid_meal_request" || targetKind === "kid_food_request") {
      setNotifsOpen(false);
      // Always open the Requests inbox so the parent gets Accept /
      // Decline actions immediately. Recipe-kind also gets a deep
      // link to the recipe in the bg so a quick "yes I'll make it"
      // tap from the inbox opens the recipe in the book.
      setKidInboxOpen(true);
      (async () => {
        const { data, error } = await supabase
          .from("kid_food_requests")
          .select("recipe_slug, request_kind, canonical_id, food_name")
          .eq("id", targetId)
          .maybeSingle();
        if (error) {
          console.warn("[notif:kid-request] lookup failed:", error.message);
          return;
        }
        // (Inbox is already open via setKidInboxOpen above. For
        // recipe-kind we ALSO stash the slug so a follow-up open-
        // recipe action from inside the inbox can deep-link.)
        const slug = data?.recipe_slug || null;
        if (data?.request_kind === "recipe" && slug) {
          // Don't auto-open the book — let the parent decide via
          // the inbox's "Make it" / Accept / Decline. The inbox
          // already exposes a per-recipe "open in book" path.
        }
      })();
      return;
    }
  }, []);

  // Lazy permission request — only on explicit bell click so we don't prompt
  // on first paint.
  const openNotifs = useCallback(() => {
    setNotifsOpen(true);
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => { /* user declined */ });
    }
  }, []);

  // Service-worker registration for Web Push. Registering doesn't
  // prompt or do anything visible — it just makes the SW available
  // so Settings → Enable can subscribe when the user opts in. Kept
  // idempotent because registering the same script twice is a no-op
  // per spec; React's Strict Mode double-invoke during dev is fine.
  //
  // The SW also dispatches `notification-tap` postMessages when a
  // user taps a push while mise is open — we route those through
  // openNotificationTarget so the deep-link behavior matches what
  // the bell panel does for the same rows.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    navigator.serviceWorker.register("/sw.js").catch(err => {
      console.error("[sw] registration failed:", err);
    });
    const onMessage = (event) => {
      const msg = event.data;
      if (!msg || msg.kind !== "notification-tap") return;
      const { target_kind, target_id } = msg.payload || {};
      if (target_kind && target_id) {
        openNotificationTarget(target_kind, target_id);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, [openNotificationTarget]);

  // Deep-link via URL query — used when the user taps a push and no
  // mise tab was open, so the SW opens a new window with
  // ?target_kind=…&target_id=…. Consume the params on first load,
  // route, then clear the URL so a refresh doesn't re-trigger.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const targetKind = params.get("target_kind");
    const targetId   = params.get("target_id");
    if (targetKind && targetId) {
      openNotificationTarget(targetKind, targetId);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [openNotificationTarget]);

  // Invite-link consumption. Three entry points all funnel into the
  // same consumeInviteCode callback:
  //   1. https://booda.app/?invite=ABC123 — shared via iMessage (web)
  //   2. https://booda.app/invite/ABC123  — path-style (SPA fallthrough)
  //   3. Native iOS appUrlOpen — same URL shapes via Universal Link
  // After consumption we fire relationships.invite() which is also
  // self-acceptance when there's already a reciprocal pending row.
  const consumeInviteCode = useCallback(async (rawCode) => {
    if (!user?.id) return;
    const cleaned = String(rawCode || "").trim().toUpperCase();
    if (!cleaned) return;
    try {
      await relationships.invite(cleaned, "family");
      pushToast("Connection request sent", { emoji: "🤝", kind: "success" });
    } catch (e) {
      pushToast(e?.message || "Couldn't add that contact", { emoji: "⚠️", kind: "warn" });
    } finally {
      if (typeof window !== "undefined") {
        window.history.replaceState({}, "", "/");
      }
    }
  }, [user?.id, relationships, pushToast]);

  // Publish the latest consumeInviteCode closure to the module-level
  // holder so the Capacitor appUrlOpen listener (which lives in
  // MainApp, mounted before this component) can dispatch into it.
  useEffect(() => {
    inviteCallbackHolder = consumeInviteCode;
    return () => {
      if (inviteCallbackHolder === consumeInviteCode) inviteCallbackHolder = null;
    };
  }, [consumeInviteCode]);

  // Drain the invite stash once per mount. The stash catches every
  // path where the code arrived before AuthedApp existed: a link
  // tapped while signed out, the web OAuth redirect that drops
  // path+query, and a signup that detoured through onboarding.
  useEffect(() => {
    const stashed = takeInvite();
    if (stashed) consumeInviteCode(stashed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same pattern for share-extension URL handoff. When iOS Share
  // Extension hands a TikTok / Instagram / Facebook recipe link to
  // the host app via app.booda.ios://share?url=…, MainApp's listener
  // parses it and dispatches here. We set the pending state + flip
  // to the Create tab; CreateMenu picks up the prop and opens
  // AddRecipeFromSheet in LINK mode pre-filled.
  useEffect(() => {
    const handler = (rawUrl) => {
      if (!rawUrl) return;
      setPendingSharedRecipeUrl(rawUrl);
      setTab("create");
    };
    // Incoming recipe-share routing (booda.app/r/<token>). Register
    // the holder, drain the cold-start stash, and — web only — parse
    // a direct /r/<token> path visit (the SPA serves index.html for
    // every path; the hash router ignores pathname, so without this
    // a browser open of a share link landed on Home with the token
    // silently discarded).
    incomingShareCallbackHolder = (token) => { if (token) setIncomingShareToken(token); };
    if (stashedShareToken) {
      const t = stashedShareToken;
      stashedShareToken = null;
      setIncomingShareToken(t);
    }
    try {
      const pathMatch = window.location?.pathname?.match(/^\/r\/([A-Za-z0-9_-]{16,})/);
      if (pathMatch) {
        setIncomingShareToken(pathMatch[1]);
        window.history.replaceState(null, "", "/");
      }
    } catch { /* SSR / exotic environments — skip */ }

    sharedUrlCallbackHolder = handler;
    // Cold-start drain — the deep link fired before this mounted.
    if (stashedShareUrl) {
      const u = stashedShareUrl;
      stashedShareUrl = null;
      handler(u);
    }
    // App-Group dead-drop pickup. The share extension WRITES the URL
    // to the group store even when iOS refuses to open the app from
    // the share sheet (the common iOS 17+ case) — so check the slot
    // on mount and on every return to foreground. Native side clears
    // it on read; a share imports exactly once.
    BoodaTimer.consumePendingShare().then((u) => { if (u) handler(u); });
    let stateSub = null;
    (async () => {
      stateSub = await CapApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) {
          BoodaTimer.consumePendingShare().then((u) => { if (u) handler(u); });
        }
      });
    })();
    return () => {
      if (sharedUrlCallbackHolder === handler) sharedUrlCallbackHolder = null;
      incomingShareCallbackHolder = null;
      stateSub?.remove?.();
    };
  }, []);

  // Web-side URL consumption now flows through the invite STASH:
  // MainApp captures ?invite= / /invite/CODE into localStorage at
  // boot (before auth resolves), and the drain effect above consumes
  // it here on mount. Reading the URL directly in this component too
  // would double-fire the invite RPC.

  // iOS APNs tap-action routing. When a user taps the OS-level banner
  // delivered by send-push's APNs branch, Capacitor fires
  // `pushNotificationActionPerformed` with the payload that the edge
  // function stamped under `data.target_kind` / `data.target_id`. We
  // hand that to the same openNotificationTarget the web bell uses so
  // a tapped push lands on the right screen regardless of platform.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let cancelled = false;
    let actionHandle;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform?.() || Capacitor.getPlatform?.() !== "ios") return;
        const { PushNotifications } = await import("@capacitor/push-notifications");
        actionHandle = await PushNotifications.addListener(
          "pushNotificationActionPerformed",
          (action) => {
            if (cancelled) return;
            const data = action?.notification?.data || {};
            const tk = data.target_kind;
            const tid = data.target_id;
            if (tk && tid) openNotificationTarget(tk, tid);
          },
        );
      } catch (e) {
        console.warn("[ios-push] action listener install failed:", e?.message || e);
      }
    })();
    return () => {
      cancelled = true;
      try { actionHandle?.remove?.(); } catch { /* ignore */ }
    };
  }, [openNotificationTarget]);

  return (
    <MCMThemeProvider>
      {/* Persistent time-of-day backdrop — sun/moon arc + drifting
          warm blobs + night-time stars. Lives at the App root so
          every major tab inherits it without each tab re-mounting
          its own. Modals and overlays render OVER it with their own
          opaque surfaces; the backdrop reads as ambient sky behind
          the page. position:fixed at inset:0 so it doesn't take up
          flow space. */}
      <WarmBackdrop />
      {/* Pinball-magic intro + loop. Plays pt-1 once on first user
          interaction, then loops pt-2. Persists across tab switches. */}
      <BackgroundMusic />
      <RadioRewards userId={user?.id || null} />
      <WeatherFX />
      <div style={{ ...pageShell }}>
      {/* Level-up ceremony — watches profile.level for upward
          movement and plays a full-screen celebration. Self-dismissing
          and zIndex 9999 so it lands on top of any open sheet. */}
      <LevelUpCeremony level={profile?.level || 1} />

      {/* Realtime XP toast stack — top-right slide-ins for non-cook
          earn events (scans, photos, reviews, badges, …). Subscribes
          to xp_events filtered to this user; suppressed by
          CookCompleteSummary via context while a beat sequence plays. */}
      <XpToastStack userId={user?.id} />

      {/* Admin quick-link — only fixed chrome left in the top bar.
          Bell + settings moved into the profile screen header; access
          them via the enlarged avatar in Home's top-right. */}
      {dbWriteError && (
        <div
          role="alert"
          onClick={() => setDbWriteError(null)}
          style={{
            position: "fixed",
            top: "calc(8px + env(safe-area-inset-top))",
            left: 12, right: 12,
            zIndex: 9000,
            padding: "10px 14px",
            background: "#3a1a1a",
            border: "1px solid #ef4444",
            color: "#fff5e6",
            borderRadius: 10,
            fontFamily: "ui-monospace, Menlo, monospace",
            fontSize: 12,
            lineHeight: 1.4,
            cursor: "pointer",
            boxShadow: "0 8px 22px rgba(0,0,0,0.45)",
          }}
        >
          <strong>DB write failed</strong> — {dbWriteError.op} on {dbWriteError.table}: {dbWriteError.error}
          <div style={{ opacity: 0.7, marginTop: 4 }}>tap to dismiss</div>
        </div>
      )}
      {profile?.role === "admin" && (
        <button
          onClick={() => setAdminOpen(true)}
          title="Open admin tools"
          aria-label="Open admin tools"
          style={{
            position: "fixed", top: 12, left: 12, zIndex: 50,
            background: COLOR.roseDeep,
            border: `1px solid ${COLOR.rose}`,
            color: COLOR.rose,
            borderRadius: 14, padding: "4px 10px",
            fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
            letterSpacing: "0.14em",
            display: "flex", alignItems: "center", gap: 6,
            cursor: "pointer",
          }}
        >
          🛠 ADMIN
        </button>
      )}

      {/* Tab switcher. Wrapping in AnimatePresence/motion so the
          active tab cross-fades with a subtle upward slide when the
          user taps the bottom bar. mode="wait" holds the next tab
          until the previous unmounts — prevents the brief stacking
          flash that a simple transition would cause. Keeping the
          animation short (120ms) so nothing feels sluggish; taps
          still register instantly. */}
      <div style={{
        paddingBottom: 80,
        // CookBanner writes its current height into --cook-banner-h,
        // updated live as dragY changes. Translate via transform —
        // not paddingTop — so the per-frame updates are GPU-
        // accelerated and skip layout/reflow entirely.
        // padding-top changes triggered a full reflow of the tab
        // content (sticky kitchen search bar, grid, nutrition rows
        // all relayout); the resulting pipeline cost dropped frames
        // and read as glitchy. transform doesn't touch layout, so
        // it tracks dragY at refresh-rate even on mobile Safari.
        // willChange: transform pre-warms the GPU layer.
        transform: "translateY(var(--cook-banner-h, 0px))",
        willChange: "transform",
      }}>
        <AnimatePresence
          mode="wait"
          initial={false}
          onExitComplete={resetScrollAfterExit}
        >
          <motion.div
            key={tab}
            // Hardware-accelerated transform string instead of
            // framer's `y` shorthand — the y prop runs on
            // requestAnimationFrame on the main thread, so under
            // load (data fetches, paint) the transition stutters.
            // Full transform strings get GPU compositing.
            initial={{ opacity: 0, transform: "translateY(8px)" }}
            animate={{
              opacity: 1,
              transform: "translateY(0px)",
              // Enter — fast, ease-out so the new screen feels
              // immediately responsive to the tab tap.
              transition: { duration: 0.22, ease: [0.23, 1, 0.32, 1] },
            }}
            exit={{
              opacity: 0,
              transform: "translateY(-6px)",
              // Exit — slightly longer + ease-in-out so the
              // opacity decay reads as a controlled fade instead
              // of a snap. Pure ease-out front-loaded the
              // opacity drop and the visible portion looked like
              // a "flash" rather than a settle.
              transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
            }}
          >
            {tab === "home"     && (
              <Home
                profile={profile}
                userId={user.id}
                familyIds={familyIds}
                familyLoading={relationships.loading}
                nameFor={nameFor}
                avatarFor={avatarFor}
                openProfile={openProfile}
                openCook={openCook}
                // Get-started checklist inputs. onOpenFindFriends
                // reuses the post-onboarding sequencer's findFriends
                // stage — same sheet, same close semantics.
                connectionsCount={relationships.family.length + relationships.friends.length}
                onOpenFindFriends={() => advancePostOnboarding("findFriends")}
                onScheduledTap={(meal) => {
                  const r = meal?.recipe_slug
                    ? findRecipe(meal.recipe_slug, findUserRecipeBySlug, meal.recipe_user_id)
                    : null;
                  if (r) {
                    // Honor the scheduled serving count. Rides as an
                    // _initialServings seed (NOT a pre-scaled copy) so
                    // CookMode's step-prose scaling keeps its base-vs-
                    // override ratio. Without this, calendar-tap cooks
                    // ran at the recipe's saved serves and the scheduled
                    // servings were ignored.
                    const seed = Number(meal?.servings);
                    const toCook = Number.isFinite(seed) && seed > 0 && seed !== Number(r.serves)
                      ? { ...r, _initialServings: seed }
                      : r;
                    openCookMode(toCook);
                  } else {
                    setTab("plan");
                  }
                }}
                // Recommendation engine inputs.
                pantry={pantry}
                // Focus Card / SnapshotPanel tap actions. Resume-cook
                // lives on CookBanner (App-level), not the Focus Card.
                onOpenAIRecipe={() => {
                  // Route to the create tab — AI is one tap from there.
                  // (Anchor-ingredient seeding is a follow-up; for now the
                  // user lands in the standard AI flow.)
                  if (tab !== "create") setPrevTab(tab);
                  setTab("create");
                }}
                onOpenCalendar={() => setTab("plan")}
                onOpenKitchen={() => setTab("pantry")}
                // Restock — user tapped the "You're out of [X]" Focus
                // Card. Push every missing staple into the shopping
                // list as a fresh row, then jump to the shopping
                // surface. One tap → ready to shop.
                onRestock={(missing) => {
                  if (!Array.isArray(missing) || missing.length === 0) return;
                  const newId = () => (typeof crypto !== "undefined" && crypto.randomUUID)
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
                  setShoppingList(prev => [
                    ...prev,
                    ...missing.map(m => ({
                      id: newId(),
                      name: m.name,
                      // "" not null — staples are disproportionately
                      // emoji-less canonicals, and an explicit null
                      // violates shopping_list_items' NOT NULL
                      // (matches the sibling onRestockItems handlers).
                      emoji: m.emoji || "",
                      amount: 1,
                      unit: "",
                      category: "pantry",
                      ingredientId: m.id,
                      source: "auto-restock",
                      priceCents: null,
                    })),
                  ]);
                  setPantryView("shopping");
                  setTab("pantry");
                }}
                onOpenNotifs={openNotifs}
                notifsUnread={notifications.unreadCount + (whatsNew.showNotification ? 1 : 0)}
                // Live cooks: Home renders FamilyKitchenView (lock-
                // screen-shape cards) above the snapshot panel + in
                // the Cooks sub-tab when activeCooks is non-empty.
                activeCooks={allActiveCooks}
                onJumpToCook={(recipe, opts) => openCookMode(recipe, opts)}
              />
            )}
            {tab === "community"  && (
              <Community
                userId={user.id}
                onCook={(recipe) => openCookMode(recipe)}
              />
            )}
            {tab === "plan"     && (
              <Plan
                profile={profile}
                userId={user.id}
                familyKey={familyKey}
                nameFor={nameFor}
                hasFamily={relationships.family.length > 0}
                family={relationships.family}
                friends={relationships.friends}
                pantry={pantry}
                setPantry={setPantry}
                shoppingList={shoppingList}
                setShoppingList={setShoppingList}
                onGoToShopping={() => { setPantryView("shopping"); setTab("pantry"); }}
                onOpenCook={openCook}
                onStartCook={openCookMode}
                deepLink={deepLink}
                onDeepLinkConsumed={() => setDeepLink(null)}
                onCreateRecipe={() => { setPrevTab(tab); setTab("create"); }}
              />
            )}
            {tab === "pantry" && pantryView === "stock" && (
              // MCM Pantry — re-skinned stock view with real Supabase
              // data. The time-of-day palette resolves through the
              // root MCMThemeProvider mounted in the AuthedApp return
              // above; no per-screen wrap needed. Tap on a card sets
              // `mcmOpenItem`, which raises the shared ItemCard
              // overlay further down — same edit flow the Kitchen
              // stock view uses, just reached from a different
              // visual. Escape hatch: `forceClassicPantry` flips the
              // view back to the original Kitchen render if
              // something here breaks (keeps users unblocked).
                <MCMKitchenScreen
                  items={pantry}
                  loading={pantryLoading}
                  onOpenItem={setMcmOpenItem}
                  // Shopping-list bridge: tapping the cart in the
                  // hero flips pantryView to "shopping", which the
                  // same tab render handles by falling through to
                  // the classic Kitchen (see below). shoppingCount
                  // feeds the badge on the cart button. Filter out
                  // purchasedAt items so the badge mirrors what the
                  // shopping screen actually shows (Shop Mode flips
                  // purchasedAt on commit; raw shoppingList.length
                  // counts historical buys too, leaving the badge
                  // stuck after a successful trip).
                  onGoToShopping={() => setPantryView("shopping")}
                  shoppingCount={Array.isArray(shoppingList) ? shoppingList.filter(it => !it?.purchasedAt).length : 0}
                  onOpenReceipts={() => setHistoryOpen(true)}
                  spendCents={spendCents}
                  // Scan + manual-add bridge. Seeds the AddDraftSheet
                  // with `{ mode: "blank" }` — empty fields. The sheet
                  // itself will offer a Scan button that re-seeds with
                  // OFF lookup results when used.
                  onOpenAdd={() => setMcmAddDraft({ mode: "blank" })}
                  // Cook-flow entry — wired to the Plan tab where the
                  // user can pick a recipe and launch CookMode. Used
                  // by MCMKitchenScreen's positive CTA when the
                  // pantry is healthy (no warn items) so a stocked
                  // user has a clear path forward instead of an
                  // empty surface.
                  onStartCooking={() => setTab("plan")}
                  onRemoveItem={(item) => {
                    // Swipe-to-remove from the MCM item card. Filters
                    // the row out of the local pantry; useSyncedList
                    // persists the delete to Supabase via the diff
                    // path. Family pantries propagate the delete via
                    // realtime to other members.
                    setPantry(prev => prev.filter(p => p.id !== item.id));
                  }}
                  onUpdateItem={(item, patch) => {
                    // Inline-edit hook for the shelf row's fill gauge
                    // (and any future inline editors). Patches the
                    // matching row in-place; useSyncedList's diff
                    // detects the field-level change and persists.
                    setPantry(prev => prev.map(p => p.id === item.id ? { ...p, ...patch } : p));
                  }}
                  onDeleteItem={(raw) => {
                    // Hold-menu Delete — same shape as MCMItemCard's
                    // onDelete (sheet path) so a row removed via the
                    // flip menu disappears identically. setPantry
                    // filter triggers the useSyncedList delete diff.
                    if (!raw?.id) return;
                    setPantry(prev => prev.filter(p => p.id !== raw.id));
                  }}
                  // Bulk cleanup — ONE setPantry filter for N rows
                  // (one sync diff) + the inverse re-add for the
                  // undo toast.
                  onDeleteItems={(rows) => {
                    const ids = new Set((rows || []).map(r => r?.id).filter(Boolean));
                    if (ids.size === 0) return;
                    setPantry(prev => prev.filter(p => !ids.has(p.id)));
                  }}
                  onRestoreItems={(rows) => {
                    if (!Array.isArray(rows) || rows.length === 0) return;
                    setPantry(prev => {
                      const have = new Set(prev.map(p => p.id));
                      return [...prev, ...rows.filter(r => r?.id && !have.has(r.id))];
                    });
                  }}
                  // Bulk Restock — same row shape as the single-item
                  // handler below, one setShoppingList write, deduped
                  // against the list AND within the batch by
                  // canonical-or-name so three cans of tomatoes
                  // produce one list row.
                  onRestockItems={(rows) => {
                    if (!Array.isArray(rows) || rows.length === 0) return;
                    setShoppingList(prev => {
                      const have = new Set(prev.map(i => i.ingredientId || (i.name || "").toLowerCase()));
                      const out = [...prev];
                      for (const raw of rows) {
                        if (!raw) continue;
                        const key = raw.canonicalId || (raw.name || "").toLowerCase();
                        if (!key || have.has(key)) continue;
                        have.add(key);
                        out.push({
                          id: typeof crypto !== "undefined" && crypto.randomUUID
                            ? crypto.randomUUID()
                            : `restock-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                          name: raw.name || "Restock",
                          emoji: raw.emoji || "",
                          amount: 1,
                          unit: raw.unit || "",
                          category: raw.category || "pantry",
                          ingredientId: raw.canonicalId || null,
                          source: "restock-from-kitchen",
                          pantryRowId: raw.id,
                        });
                      }
                      return out;
                    });
                  }}
                  onRestockItem={(raw) => {
                    // Hold-menu Restock — append a fresh shopping row
                    // for this canonical. Mirrors the Focus Card's
                    // auto-restock shape (see line ~1269) so the
                    // shopping screen renders this identically. The
                    // `pantryRowId` link lets us trace the origin
                    // back to the kitchen card if we ever want a
                    // "restocked from X" badge.
                    if (!raw) return;
                    setShoppingList(prev => ([
                      ...prev,
                      {
                        id: typeof crypto !== "undefined" && crypto.randomUUID
                          ? crypto.randomUUID()
                          : `restock-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                        name: raw.name || "Restock",
                        emoji: raw.emoji || "",
                        amount: 1,
                        unit: raw.unit || "",
                        category: raw.category || "pantry",
                        ingredientId: raw.canonicalId || null,
                        source: "restock-from-kitchen",
                        pantryRowId: raw.id,
                      },
                    ]));
                  }}
                  hideDock
                />
            )}
            {/* MCM shopping screen — the cart, the to-buy list, the
                "Add to list" + "Shop Mode" entry points. Theme
                resolves through the root MCMThemeProvider; no
                per-screen wrap. Shop Mode hands off to the legacy
                flow temporarily (it lives inside Kitchen.jsx) by
                flipping forceClassicPantry; once Shop Mode is
                lifted to App level we can drop that handoff. */}
            {tab === "pantry" && pantryView === "shopping" && (
                <MCMShoppingScreen
                  shoppingList={shoppingList}
                  spendCents={spendCents}
                  viewerId={user.id}
                  nameFor={nameFor}
                  avatarFor={avatarFor}
                  onOpenReceipts={() => setHistoryOpen(true)}
                  onBackToKitchen={() => setPantryView("stock")}
                  onAdd={() => setMcmAddDraft({ mode: "wizard" })}
                  // Rapid-fire text jotting. Moved here from
                  // CreateMenu's hero-card slot when the two add
                  // cards collapsed into one — the list screen is
                  // where "capers, saffron" belongs anyway.
                  onQuickAdd={() => setShoppingQuickAddOpen(true)}
                  onShopMode={() => {
                    // ShopMode is mounted at App level (Phase 3
                    // step 1). The pendingPantryAction effect at
                    // App.jsx flips shopModeOpen=true on its own.
                    // No need to drop into the legacy Kitchen view.
                    setPendingPantryAction("shopMode");
                  }}
                  onCheckOff={(sItem) => {
                    // Mirror legacy checkOffShoppingItem: mint a
                    // pantry row from the picked-up shopping entry,
                    // remove from the list. (Merge-into-existing-row
                    // logic stays in the legacy path for v1; this
                    // covers the common "manual jot → fresh row"
                    // case.)
                    setPantry(prev => [...prev, {
                      id: crypto.randomUUID(),
                      ingredientId: sItem.ingredientId || null,
                      name: sItem.name,
                      emoji: sItem.emoji || "🥫",
                      amount: sItem.amount,
                      unit: sItem.unit,
                      max: 0,
                      category: sItem.category || "pantry",
                      lowThreshold: Math.max((sItem.amount || 0) * 0.25, 0.25),
                      purchasedAt: new Date(),
                    }]);
                    setShoppingList(prev => prev.filter(i => i.id !== sItem.id));
                  }}
                  onRemove={(id) => setShoppingList(prev => prev.filter(i => i.id !== id))}
                />
            )}
            {/* Classic Kitchen mount removed (Phase 3 final step):
                forceClassicPantry was the only path that mounted it,
                and the only setter (`setForceClassicPantry(true)`)
                has been removed since ShopMode now mounts at App
                level via pendingPantryAction. pantryView only ever
                takes "stock" | "shopping", so the gate condition
                never evaluates true. Kitchen.jsx and ItemCard.jsx
                are now dead code, deletable in the next pass. */}
            {tab === "create" && (
              <CreateMenu
                userId={user.id}
                profile={profile}
                familyKey={familyKey}
                kitchenTracking={kitchenTracking}
                pantry={pantry}
                setPantry={setPantry}
                shoppingList={shoppingList}
                setShoppingList={setShoppingList}
                onGoToShopping={() => { setPantryView("shopping"); setTab("pantry"); }}
                family={relationships.family}
                friends={relationships.friends}
                onClose={() => setTab(prevTab)}
                onCooked={() => setProfileUserId(user.id)}
                onStartCook={openCookMode}
                onBackgroundGenerate={startBackgroundRecipeGen}
                onOpenRecipeBook={() => {
                  setRecipeBookState({ initialAddOpen: false });
                }}
                onOpenAddRecipeSheet={() => {
                  setRecipeBookState({ initialAddOpen: true });
                }}
                onOpenAddDraft={(seed) => {
                  // Default = blank. CreateMenu's "+ IN PANTRY"
                  // button on AIRecipe ingredient rows passes a
                  // seed object { mode, name, canonicalId,
                  // initialAmount } so the user lands directly
                  // on the manual-add surface with the ingredient
                  // identity prefilled (e.g. "Nutmeg" already in
                  // the form when they clicked + IN PANTRY on
                  // a missing nutmeg row).
                  setMcmAddDraft(seed && typeof seed === "object" && seed.mode
                    ? seed
                    : { mode: "blank" });
                }}
                onOpenEatQuick={() => {
                  setEatQuickOpen(true);
                  setTab("home");
                }}
                onRequestPantryAction={(kind) => {
                  setPendingPantryAction(kind);
                  setTab("pantry");
                }}
                initialAiSeed={pendingAiPhotoSeed}
                initialAiSketch={pendingAiPhotoSketch}
                onAiSeedConsumed={() => {
                  setPendingAiPhotoSeed(null);
                  setPendingAiPhotoSketch(null);
                }}
                initialSharedRecipeUrl={pendingSharedRecipeUrl}
                onSharedRecipeUrlConsumed={() => setPendingSharedRecipeUrl(null)}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* MCM Pantry edit overlay — MCMItemCard. Looks up the fresh
          pantry row by id so realtime edits from the family flow
          through without a close+reopen. */}
      {/* AnimatePresence keeps the sheet mounted long enough for its
          exit transition to play — without this wrapper, the
          motion.div unmounts the moment mcmOpenItem flips false and
          the slide-down-off-screen exit never runs. */}
      <AnimatePresence>
        {mcmOpenItem && (() => {
          const fresh = pantry.find(p => p.id === mcmOpenItem.id);
          if (!fresh) {
            // Row was deleted (from this client or a family member).
            // Close the overlay on next tick so we don't rip the
            // modal out from under a mid-edit user; setTimeout = 0
            // defers to avoid a setState-during-render warning.
            setTimeout(() => setMcmOpenItem(null), 0);
            return null;
          }
          return (
            <MCMItemCard
              key={fresh.id}
              item={fresh}
              pantry={pantry}
              userId={user.id}
              isAdmin={isAdmin}
              familyIds={familyIds}
              onUpdate={(patch) => setPantry(prev => prev.map(p => (
                p.id === fresh.id ? { ...p, ...patch } : p
              )))}
              onDelete={() => {
                setPantry(prev => prev.filter(p => p.id !== fresh.id));
                setMcmOpenItem(null);
              }}
              onClose={() => setMcmOpenItem(null)}
            />
          );
        })()}
      </AnimatePresence>

      {/* Older ReceiptHistoryModal mount (used to live here for the
          MCM hero's receipt button) consolidated into the single
          App-level mount further down — both entry points now flip
          the same `historyOpen` state and route through the new
          `setOpenReceiptId` path. */}

      {/* MCM shopping quick-add — name + qty input, themed in the
          MCM warm-palette language. Theme resolves through the root
          MCMThemeProvider; no per-modal wrap. Silent canonical
          inference at submit attaches an ingredientId when the typed
          text resolves — gives the check-off → kitchen flow enough
          signal to route the eventual kitchen row to the right tile
          without ever asking the user. */}
      <AnimatePresence>
        {mcmShoppingAddOpen && (
          <MCMShoppingAdd
            onClose={() => setMcmShoppingAddOpen(false)}
            onAdd={(item) => {
              setShoppingList(prev => [
                ...prev,
                { ...item, source: item.source || "manual" },
              ]);
              setMcmShoppingAddOpen(false);
            }}
            onUseWizard={() => {
              setMcmShoppingAddOpen(false);
              setMcmAddDraft({ mode: "wizard" });
            }}
          />
        )}
      </AnimatePresence>

      {/* MCM scan + manual-add sheet. Theme resolves through the
          root MCMThemeProvider. On submit, the sheet hands back a
          complete pantry-row shape and we append it via setPantry —
          useSyncedList's diff path persists the insert to Supabase
          automatically. */}
      <AnimatePresence>
        {mcmAddDraft && (
          <MCMAddDraftSheet
            seed={mcmAddDraft}
            userId={user.id}
            isAdmin={isAdmin}
            onClose={() => setMcmAddDraft(null)}
            onSubmit={(row) => {
              setPantry(prev => [...prev, {
                id: crypto.randomUUID(),
                purchasedAt: new Date(),
                ...row,
              }]);
              setMcmAddDraft(null);
              setAddSuccess({
                mode: row.barcodeUpc ? "scanner" : "manual",
                row,
              });
            }}
            onAddToShoppingList={(listRow) => {
              setShoppingList(prev => [...(prev || []), listRow]);
              setMcmAddDraft(null);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {addSuccess && (
          <SuccessOverlay
            mode={addSuccess.mode}
            row={addSuccess.row}
            onDone={() => setAddSuccess(null)}
          />
        )}
      </AnimatePresence>

      {settingsOpen && (
        <Settings
          userId={user.id}
          profile={profile}
          relationships={relationships}
          upsertProfile={upsertProfile}
          avatarCatalog={avatars.catalog}
          ownedAvatars={avatars.owned}
          onPinAvatar={async (slug) => {
            await avatars.pin(slug);
            const cat = avatars.catalog.find(c => c.slug === slug);
            patchProfile({ avatar_slug: slug, avatar_url: cat?.image_url || null });
          }}
          onClose={() => setSettingsOpen(false)}
          onOpenProfile={openProfile}
          // Re-open release notes from Settings — covers both casual
          // re-reading and the silent first-paint heuristic in
          // useWhatsNew (new users get notes silently marked-as-seen
          // and need this entry to ever see them).
          onOpenReleaseNotes={() => { setSettingsOpen(false); whatsNew.openFromSettings(); }}
          onOpenAdmin={() => { setSettingsOpen(false); setAdminOpen(true); }}
          onOpenSocial={() => { setSettingsOpen(false); setSocialOpen(true); }}
          onEnterKidMode={(kidId) => { setSettingsOpen(false); onEnterKidMode?.(kidId); }}
        />
      )}

      {socialOpen && (
        <SocialPage
          userId={user.id}
          profile={profile}
          upsertProfile={upsertProfile}
          relationships={relationships}
          onClose={() => setSocialOpen(false)}
          onOpenProfile={openProfile}
        />
      )}

      {adminOpen && (
        <AdminPanel
          userId={user.id}
          onClose={() => setAdminOpen(false)}
        />
      )}

      {profileUserId && (
        <UserProfile
          targetUserId={profileUserId}
          viewerId={user.id}
          relationship={relationshipFor(profileUserId)}
          familyKey={familyKey}
          nameFor={nameFor}
          deepLink={deepLink}
          onConsumeDeepLink={() => setDeepLink(null)}
          onOpenProfile={openProfile}
          // Both handlers close the profile before opening their
          // overlay — Settings + UserProfile are both zIndex:200, so
          // stacking them makes the newer one disappear behind this
          // one. Same goes for NotificationsPanel (zIndex:100, would
          // land behind us). Close first, land clean.
          onOpenSettings={() => { setProfileUserId(null); setSettingsOpen(true); }}
          onOpenSocial={() => { setProfileUserId(null); setSocialOpen(true); }}
          onOpenCook={(cookId) => {
            // Cookbook is now an overlay inside UserProfile, so we
            // stay on this profile and just hand the deep link in —
            // the embedded Cookbook opens the detail once it resolves.
            setDeepLink({ kind: "cook_log", id: cookId });
          }}
          // Recipe Book wiring. onStartCook lets the book launch
          // CookMode with a saved recipe; hasFamily gates the SHARE
          // pill on each spread.
          onStartCook={(recipe) => {
            setProfileUserId(null);
            openCookMode(recipe);
          }}
          hasFamily={relationships.family.length > 0}
          family={relationships.family}
          friends={relationships.friends}
          onClose={() => setProfileUserId(null)}
        />
      )}

      {notifsOpen && (
        <NotificationsPanel
          notifications={notifications.notifications}
          loading={notifications.loading}
          unreadCount={notifications.unreadCount}
          markAllRead={notifications.markAllRead}
          dismiss={notifications.dismiss}
          clearAll={notifications.clearAll}
          onClose={() => setNotifsOpen(false)}
          onOpen={openNotificationTarget}
          // Pinned release-notes entry — only when there's an
          // unacknowledged release. whatsNew.showNotification is the
          // single source of truth for "user hasn't seen this version
          // yet" — if the slim modal is/was eligible to fire, the
          // panel pin is too. Tap routes to the full modal (closes
          // panel first so the modal isn't buried), dismiss marks
          // the version as seen via useWhatsNew.
          pinned={whatsNew.showNotification ? [{
            id: `release-${whatsNew.latestVersion || "current"}`,
            emoji: "📋",
            msg: whatsNew.latestTitle || "What's new in this release",
            sublabel: `v${whatsNew.latestVersion || ""} · TAP TO READ`,
            onTap: () => { setNotifsOpen(false); whatsNew.openFromSettings(); },
            onDismiss: () => whatsNew.dismiss(),
          }] : []}
          // Always-available footer link to browse past notes anytime.
          // Closes the panel first so the modal lands clean.
          onOpenReleaseNotes={() => { setNotifsOpen(false); whatsNew.openFromSettings(); }}
        />
      )}

      {/* Bottom tab bar — four regular tabs (HOME · COURSES · CALENDAR ·
          KITCHEN) with a floating ➕ button slotted between tabs 2 and
          3. The ➕ isn't a tab — tapping it opens CreateMenu as a full-
          screen overlay, the universal creation hub for cooking and
          pantry ingress. */}
      {/* Bottom nav — MCM glass-morphism floating pill, mirroring the
          FloatingLocationDock's treatment in the Kitchen screen so the
          two docks read as siblings instead of a translucent bar
          stacked over a translucent pill. Centered, padded off the
          home-indicator, glassFillHeavy + backdrop-blur, full-pill
          radius. */}
      <DockGooFilter />
      <div ref={dockRef} style={{
        position:"fixed",
        bottom:"calc(16px + env(safe-area-inset-bottom))",
        left:"50%",
        transform:"translateX(-50%)",
        zIndex: 100,
        // Glass pill — lighter blur lets shapes behind read more
        // distinctly; bumped saturation pulls the warm cream / sky
        // tones through with stronger color than a soft frost.
        background:"rgba(255,250,240,0.40)",
        backdropFilter:"blur(10px) saturate(200%)",
        WebkitBackdropFilter:"blur(10px) saturate(200%)",
        border:"1px solid rgba(255,255,255,0.6)",
        borderRadius:999,
        boxShadow:"0 18px 40px rgba(30,20,8,0.16), 0 4px 12px rgba(30,20,8,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
        // Hide the nav whenever a full-screen entry sheet is open
        // (AddDraftSheet, MCM shopping add, the create menu, the
        // item-edit overlay). The sheet covers the page above the
        // backdrop but below z:100, so the nav otherwise sits over
        // the bottom of the form (Where does it go? + Cancel/Submit,
        // or MCMItemCard's Delete / Done footer) and blocks taps.
        display: (mcmAddDraft != null || mcmShoppingAddOpen || mcmOpenItem)
          ? "none"
          : "flex",
        alignItems:"center",
        gap:2,
        padding:"4px 6px",
        // position:relative anchors the absolute-positioned indicator
        // layer below to the dock's coordinate space, so blob `x` and
        // `y` values resolve against the dock — not the viewport.
        // Without this, fixed-positioning's auto-anchoring would put
        // the indicator at viewport (0,0). The fixed-positioning of
        // the dock itself isn't broken — relative wins for descendants.
      }}>
        {/* Sliding goo indicator — animates between every dock target,
            including the center + button (whose ref is registered
            under id "create" via setDockTabRef below). When tab
            flips to create, the goo morphs over and wraps the +. */}
        <DockIndicator rect={dockTargetRect} />

        {/* Three-segment dock layout: left-tab cluster, centered +
            button, right-tab cluster. flex:1 on the side groups makes
            the + button truly center-aligned regardless of label
            widths (HOME / COURSES vs CALENDAR / KITCHEN can drift on
            font load without dragging the + off-center). */}
        <div style={{ display: "flex", flex: 1, justifyContent: "flex-end", gap: 2 }}>
          {nav.slice(0, navSplit).map(({ id, label }) => (
            <DockTab key={id} id={id} label={label} active={tab === id} onSelect={setTab} buttonRef={setDockTabRef(id)} />
          ))}
        </div>

        {/* Floating ➕ — burnt-orange CTA pill that lifts above the
            dock and is sized to fully cover the goo indicator pill
            when active. */}
        <motion.button
          // Register under id "create" so the goo indicator can find
          // this button when measuring the active-tab rect.
          ref={setDockTabRef("create")}
          onClick={() => {
            // CreateMenu is now a tab destination, not an overlay.
            // Cache the current tab so Close can return there.
            if (tab !== "create") setPrevTab(tab);
            setTab("create");
          }}
          title="Create"
          aria-label="Create"
          transition={{ type: "spring", stiffness: 600, damping: 28 }}
          style={{
            // Bumped to 52×52 — the previous 40×40 left a visible
            // ring of the goo indicator poking out around the
            // button's edge when the create tab was active. 52
            // covers the indicator fully while keeping the lift +
            // proportions. fontSize raised to match.
            width: 52, height: 52, borderRadius: 26,
            background: "linear-gradient(180deg, #C85A1F 0%, #A34711 100%)",
            color: "#FFF8EE",
            border: "1px solid rgba(255,255,255,0.35)",
            cursor: "pointer",
            fontSize: 28, lineHeight: 1, fontWeight: 300,
            display: "flex", alignItems: "center", justifyContent: "center",
            y: -12,
            margin: "0 6px",
            flexShrink: 0,
            boxShadow: "0 14px 30px rgba(168,73,17,0.42), 0 2px 6px rgba(168,73,17,0.28)",
            // zIndex 1 — above the goo indicator layer so the +
            // glyph stays crisp even when the indicator is parked
            // behind it.
            position: "relative",
            zIndex: 1,
          }}
        >
          +
        </motion.button>

        <div style={{ display: "flex", flex: 1, justifyContent: "flex-start", gap: 2 }}>
          {nav.slice(navSplit).map(({ id, label }) => (
            <DockTab key={id} id={id} label={label} active={tab === id} onSelect={setTab} buttonRef={setDockTabRef(id)} />
          ))}
        </div>
      </div>

      {/* CreateMenu is rendered inline as a tab page above (search
          for `tab === "create"`). The previous overlay mount has been
          removed — the + button now sets `tab = "create"`, which
          renders CreateMenu inside the App's tab AnimatePresence with
          the same cross-fade as Home / Plan / Kitchen, and Close
          returns to whichever tab the user came from via prevTab. */}

      {/* Parent-facing kid requests inbox. Top-level so tapping a
          kid_food_request / kid_meal_request notification opens
          Accept / Decline / Make-it actions immediately instead of
          having to navigate to Settings → Kids Mode. */}
      {kidInboxOpen && (
        <KidRequestsInbox
          userId={user.id}
          onClose={() => setKidInboxOpen(false)}
          onOpenRecipeBook={(slug) => {
            setKidInboxOpen(false);
            setRecipeBookState({ initialAddOpen: false, initialSlug: slug || null });
          }}
          onCookRequest={async (req) => {
            // Resolve the request's recipe: by slug (bundled or user)
            // first, then by the generated user_recipes id. Starting
            // the cook fires DB trigger 0261, which links the request
            // to the new session → the kid's strip flips to 🔥
            // "Cooking now".
            let r = req?.recipe_slug
              ? findRecipe(req.recipe_slug, findUserRecipeBySlug)
              : null;
            if (!r && req?.generated_recipe_id) {
              r = userRecipeRows.find((x) => x.id === req.generated_recipe_id)?.recipe || null;
            }
            if (!r) throw new Error("No recipe yet — tap MAKE A RECIPE first");
            setKidInboxOpen(false);
            openCookMode(r, { view: "cook" });
          }}
        />
      )}

      {/* App-level Recipe Book mount. Opened by CreateMenu's "Pick
          a recipe" + "Write your own" branches. UserProfile still
          mounts its own instance via the avatar menu — they're
          independent state. */}
      {recipeBookState && (
        <RecipeBook
          open={!!recipeBookState}
          onClose={() => setRecipeBookState(null)}
          userId={user.id}
          viewerName={profile?.name?.split(/\s+/)[0] || ""}
          hasFamily={relationships.family.length > 0}
          isAdmin={isAdmin}
          // Pantry-off mode: RecipeBookIndex suppresses the READY /
          // N-MISSING badge column outright when pantry is empty
          // (computeRecipeMatch totals RECIPE ingredients, so without
          // that guard every row would read all-MISSING — noise for a
          // user who deliberately doesn't track inventory).
          pantry={kitchenTracking ? pantry : []}
          viewerAllergies={Array.isArray(profile?.allergies) ? profile.allergies : []}
          initialAddOpen={!!recipeBookState.initialAddOpen}
          initialSlug={recipeBookState.initialSlug || null}
          onRetryGenerate={retryBackgroundGen}
          onCook={(recipe) => {
            setRecipeBookState(null);
            openCookMode(recipe);
          }}
          onSchedule={(recipe) => {
            // Don't close the book — SchedulePicker portals on top
            // of it. The user lands back on the same recipe page
            // after they save / cancel.
            setAppSchedulingRecipe(recipe);
          }}
          onPhotoSeed={(items, extras) => {
            setRecipeBookState(null);
            setPendingAiPhotoSeed(items);
            // finished_dish mode also carries a sketch (dishName +
            // structure + techniques + finishing + plating). Stash it
            // alongside the items so CreateMenu can pre-fill the meal
            // prompt with the visible characteristics.
            setPendingAiPhotoSketch(extras?.sketch || null);
            setTab("create");
          }}
        />
      )}
      {appSchedulingRecipe && (
        <SchedulePicker
          recipe={appSchedulingRecipe}
          userId={user.id}
          userName={profile?.name?.split(/\s+/)[0] || ""}
          family={relationships.family}
          friends={relationships.friends}
          defaultRequest={false}
          onClose={() => setAppSchedulingRecipe(null)}
          onSave={async ({ scheduledFor, notificationSettings, note, cookId, isRequest, servings, mealSlot, eatAnchor, helperIds, dinerIds }) => {
            try {
              const meal = await scheduleAppMeal({
                recipeSlug: appSchedulingRecipe.slug,
                recipeUserId: appSchedulingRecipe._userId || user.id,
                scheduledFor,
                notificationSettings,
                note,
                cookId,
                isRequest,
                servings,
                mealSlot,
                eatAnchor,
              });
              pushToast("Scheduled — see the calendar tab", { emoji: "📅", kind: "info" });

              // Team cooks (migration 0173). Pre-create the cook_session
              // + participant rows so notifications fan out immediately
              // and the lead/helpers can see each other before anyone
              // taps START. Fire-and-forget — failures here don't undo
              // the scheduled_meals insert.
              try {
                const hasTeam = (helperIds?.length || 0) + (dinerIds?.length || 0) > 0;
                if (hasTeam && meal?.id) {
                  const leadId = cookId || user.id;
                  const { data: session, error: csErr } = await supabase
                    .from("cook_sessions")
                    .insert({
                      user_id:      leadId,
                      recipe_slug:  appSchedulingRecipe.slug,
                      recipe_title: appSchedulingRecipe.title,
                      recipe_emoji: appSchedulingRecipe.emoji,
                      status:       "active",
                      ...(Number.isFinite(servings) ? { servings } : {}),
                    })
                    .select()
                    .single();
                  if (csErr) throw csErr;
                  const seen = new Set();
                  const rows = [];
                  const pushRow = (uid, role) => {
                    if (!uid || seen.has(uid)) return;
                    seen.add(uid);
                    rows.push({
                      cook_session_id: session.id,
                      user_id:         uid,
                      role,
                      color_tag:       "",            // BEFORE-INSERT trigger picks one
                      invited_by:      user.id,
                      ...(uid === user.id ? { accepted_at: new Date().toISOString() } : {}),
                    });
                  };
                  pushRow(leadId, "lead");
                  for (const uid of (helperIds || [])) pushRow(uid, "helper");
                  for (const uid of (dinerIds || []))  pushRow(uid, "diner");
                  const { error: pErr } = await supabase
                    .from("cook_session_participants")
                    .insert(rows);
                  if (pErr) throw pErr;
                }
              } catch (teamErr) {
                console.warn("[app] team-cook setup failed:", teamErr?.message || teamErr);
              }
            } catch (e) {
              console.error("[app] schedule failed:", e);
              pushToast(`Couldn't schedule: ${e?.message || "try again"}`, { emoji: "⚠️", kind: "warn" });
            }
          }}
        />
      )}

      {/* Post-onboarding stage 1: the swipeable app tour, chapter set
          picked by the user's cooking-style persona. Done/skip hands
          off to stage 2 (find friends). */}
      {postOnboarding === "tour" && (
        <OnboardingTour
          cookStyle={profile?.cook_style || null}
          onDone={() => advancePostOnboarding("findFriends")}
        />
      )}

      {/* Post-onboarding stage 2: contact-match auto-launch. Sheet
          auto-runs the scan on open via autoStart (native) or lands
          straight in share-invite mode (web / zero matches). */}
      {autoFindFriends && (
        <FindFriendsSheet
          open={autoFindFriends}
          onClose={() => setAutoFindFriends(false)}
          userId={user.id}
          invite={relationships.invite}
          inviteCode={profile?.invite_code || null}
          viewerName={(profile?.name || "").split(/\s+/)[0] || ""}
          autoStart
          existingIds={new Set([
            ...relationships.family.map((r) => r.otherId),
            ...relationships.friends.map((r) => r.otherId),
            ...relationships.incoming.map((r) => r.otherId),
            ...relationships.outgoing.map((r) => r.otherId),
          ])}
        />
      )}

      {/* App-level ShoppingQuickAdd mount. Opened by the shopping
          screen's JOT button (was CreateMenu's "Add to shopping
          list" hero card until the two add cards collapsed into
          one). Kitchen still mounts its own copy for the in-page
          shopping flow. */}
      <AnimatePresence>
        {shoppingQuickAddOpen && (
          <ShoppingQuickAdd
            key="app-shopping-quick-add"
            onClose={() => setShoppingQuickAddOpen(false)}
            onAdd={item => setShoppingList(prev => [...prev, { ...item, source: item.source || "manual" }])}
          />
        )}
      </AnimatePresence>

      {/* Eat — pantry-less calorie log. Opened by CreateMenu's "Eat"
          card. Writes consumption_logs directly (no pantry decrement)
          and optionally appends to shopping list when the user flags
          that they finished the package. */}
      {(incomingShareToken || incomingShareId) && (
        <IncomingShareSheet
          token={incomingShareToken}
          shareId={incomingShareId}
          saveRecipe={saveUserRecipe}
          onClose={() => { setIncomingShareToken(null); setIncomingShareId(null); }}
        />
      )}
      <AnimatePresence>
        {eatQuickOpen && (
          <EatQuickSheet
            key="app-eat-quick"
            userId={user.id}
            pantry={pantry}
            shoppingList={shoppingList}
            setShoppingList={setShoppingList}
            onClose={() => setEatQuickOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Shop Mode — full rebuild. Owns scan/resolve/checkout phases
          internally; closes back to wherever the user opened it from
          via onClose. */}
      {shopModeOpen && (
        <ShopMode
          userId={user.id}
          shoppingList={shoppingList}
          onRemoveShoppingItem={(id) =>
            setShoppingList((prev) => (prev || []).filter((it) => it.id !== id))
          }
          onClose={() => setShopModeOpen(false)}
        />
      )}

      {/* Phase 3 step 4 — ReceiptView + ReceiptHistoryModal. Single
          source of truth for receipt surfaces. Triggered from:
            1. Deep-link push (handled via the effect above)
            2. Kitchen's classic ItemCard provenance taps (callback
               into App via onOpenReceipt prop on Kitchen)
            3. Kitchen's classic shopping-fallback "history" button
               (callback into App via onOpenReceiptHistory)
            4. CookComplete / Profile / any other surface that wants
               to surface a receipt by id */}
      {openReceiptId && (
        <ReceiptView
          receiptId={openReceiptId.receiptId}
          scanId={openReceiptId.scanId}
          pantry={pantry}
          userId={user.id}
          familyIds={familyIds}
          onOpenItem={(item) => setMcmOpenItem(item)}
          onClose={() => setOpenReceiptId(null)}
        />
      )}
      {historyOpen && (
        <ReceiptHistoryModal
          userId={user.id}
          onOpenReceipt={(id) => setOpenReceiptId({ receiptId: id })}
          onOpenTrips={() => { setHistoryOpen(false); setTripsHistoryOpen(true); }}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {tripsHistoryOpen && (
        <TripHistoryModal
          userId={user.id}
          refreshKey={tripsRefreshKey}
          onOpenTrip={(id) => setOpenTripId(id)}
          onClose={() => setTripsHistoryOpen(false)}
        />
      )}
      {openTripId && (
        <TripDetailModal
          tripId={openTripId}
          userId={user.id}
          onOpenReceipt={(id) => { setOpenTripId(null); setOpenReceiptId({ receiptId: id }); }}
          onAttachComplete={() => setTripsRefreshKey((n) => n + 1)}
          onClose={() => setOpenTripId(null)}
        />
      )}

      {/* Pinned "cooking in progress" banner. Reads live state from
          useActiveCookSession; renders only when there IS an active
          cook and the user hasn't dismissed. Tap re-opens CookMode
          at the live step; × hides until next session change. Mounted
          outside AnimatePresence because it should be visible across
          every tab, including during tab cross-fades. */}
      <CookBanner
        active={activeCook}
        onResume={resumeCookFromBanner}
        onDismiss={activeCook.dismiss}
        // Banner-as-switcher: when more than one cook is active, the
        // banner renders a numeric tab row at the top. Tap a tab →
        // sets focusedCookSessionId, the singular hook re-derives
        // the focused cook from the plural list, banner re-renders
        // showing that cook's recipe / step / timer.
        cooks={allActiveCooks}
        focusedSessionId={focusedCookSessionId || activeCook?.session?.id || null}
        onFocusCook={(sid) => {
          setFocusedCookSessionId(sid);
          // Bump this cook's iOS Live Activity priority so the Dynamic
          // Island compact slot swaps to match the banner focus. The
          // user's attention just shifted; the island should follow.
          // Fire-and-forget; non-iOS-native is a no-op upstream.
          if (sid) {
            BoodaTimer.focusActivity({ clientId: `cook-${sid}` })
              .catch((e) => console.warn("[app] focusActivity failed:", e?.message || e));
          }
        }}
      />

      {/* Lapsed-cook closure — cooks that outlived their live window
          without DONE! LOG IT or an abandon. A timer never closes a
          cook; the user does. Gated on no live banner so the two
          never stack (the live cook resolves first, then this
          surfaces). FINISH & LOG resumes via the same 3-tier banner
          path; DIDN'T COOK abandons honestly. */}
      {!activeCook.session && !cookModeRecipe && (
        <LapsedCookBanner
          lapsed={activeCook.lapsedSessions}
          onResume={(recipe, session) => finishLapsedCook(recipe, session)}
          onDiscard={(sessionId) => {
            activeCook.dismissLapsed(sessionId);
            abandonCook(sessionId);
          }}
          onDismiss={(sessionId) => activeCook.dismissLapsed(sessionId)}
        />
      )}


      {/* App-level CookMode overlay. One mount, shared by every
          entry point (CreateMenu's start-cooking, Plan's Cook Now,
          cook_session deeplinks, the pinned banner's resume tap).
          Full-screen over the page shell; z-index above the banner +
          nav, below modal sheets (160). onExit just closes the
          overlay — the cook_sessions row stays active for 2h so the
          banner can surface it. onDone (finalize) closes the overlay
          AND lands the user on their profile so the fresh cook_log
          shows up. */}
      {/* AnimatePresence so CookMode opens with a slide-up/fade and
          minimizes with a slide-down/fade instead of disappearing.
          mode is omitted intentionally — CookMode is a single
          conditional, no sibling component to wait on. */}
      <AnimatePresence>
      {cookModeRecipe && (
        <motion.div
          key="cook-mode-overlay"
          // Enter: the banner EXPANDS downward into the full recipe.
          // Starts at scaleY 0.08 (banner-height-equivalent) anchored
          // to the top, grows to fill the viewport. Mirrors the exit
          // animation so open / close feel like the same continuous
          // transform between two surfaces — the banner unfolds into
          // the cook view, then folds back up when minimized.
          initial={{ scaleY: 0.08, y: 0 }}
          animate={{
            scaleY: 1,
            y: 0,
            transition: { duration: 0.46, ease: [0.32, 0.72, 0, 1] },
          }}
          exit={{
            // Squish the BOX downward into the banner's position,
            // THEN fade it out once it has arrived. Two-stage
            // keyframe:
            //   0   → 80%  : scaleY 1 → 0.08, opacity stays 1
            //                 (box collapses while staying opaque)
            //   80% → 100% : opacity 1 → 0
            //                 (box dissolves at banner height,
            //                  revealing the actual CookBanner
            //                  that's already underneath at top:0)
            // Contents (the inner motion.div) fade in the first
            // ~30% so by the time the box is thin enough for
            // distortion to show, the text is already gone.
            scaleY: [1, 0.08, 0.08],
            opacity: [1, 1, 0],
            y: 0,
            transition: {
              duration: 0.6,
              ease: [0.32, 0.72, 0, 1],
              times: [0, 0.78, 1],
            },
          }}
          style={{
            position: "fixed", inset: 0, maxWidth: 480, margin: "0 auto",
            background: COLOR.night, color: COLOR.ink,
            zIndex: 140, overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            transformOrigin: "top center",
          }}
        >
        <motion.div
          // Inner content wrapper. Drives the contents-only fade so
          // the box (cream fill) stays opaque while the recipe text
          // appears/disappears.
          //
          // Enter: text fades IN after the box has finished expanding
          // (delay matches roughly half the parent's expand duration)
          // so the user sees the banner unfold to full height first,
          // THEN the recipe materializes inside. Avoids the awkward
          // visual of warped text scaling with the box.
          //
          // Exit: text fades OUT fast (16ms) at the start so by the
          // time the box has shrunk to banner height, contents are
          // already gone — no distortion visible mid-collapse.
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1], delay: 0.24 },
          }}
          exit={{
            opacity: 0,
            transition: { duration: 0.16, ease: [0.4, 0, 0.2, 1] },
          }}
        >
          <CookMode
            // Force a clean remount when the focused recipe (or the
            // joined session) changes. Without this, switching from
            // cook A to cook B re-uses the SAME CookMode instance and
            // mutates its `recipe` prop in-place — telemetry refs,
            // useEffect dep chains, Timer mounts, etc. all carry over
            // from the prior recipe and crash. Keyed on slug + join
            // id so each (recipe, session) combo is its own mount.
            //
            // resumeSessionId + finishNow ride in the key too: a lapsed
            // "FINISH & LOG" reuses the same slug, so without them the
            // SECOND finish of the same recipe reused the instance with
            // finalizedRef / finishNowFiredRef already true from the
            // first finish — the auto-finish effect never re-fired and
            // the post-cook screen never appeared ("didn't fire AGAIN").
            // A fresh key per finish intent guarantees clean refs.
            key={`${cookModeRecipe?.slug || ""}::${cookModeJoinSessionId || ""}::${cookModeResumeSessionId || ""}::${cookModeFinishNow ? "fin" : ""}`}
            recipe={cookModeRecipe}
            initialView={cookModeView}
            initialStepIndex={cookModeStepIdx}
            initialTimerEndsAt={cookModeEndsAt}
            initialTimerStageIdx={cookModeStageIdx}
            joinSessionId={cookModeJoinSessionId}
            // ALL COOKS button inside CookMode navigates to the
            // Home tab where the FamilyKitchenView card list lives.
            // CookMode auto-minimizes (closeCookMode) so the user
            // lands on Home with the cards visible.
            onOpenFamilyKitchen={() => {
              closeCookMode();
              setTab("home");
            }}
            onExit={closeCookMode}
            onAbandon={abandonCook}
            onDone={() => {
              closeCookMode();
              setProfileUserId(user.id);
            }}
            pantry={pantry}
            setPantry={setPantry}
            shoppingList={shoppingList}
            setShoppingList={setShoppingList}
            onGoToShopping={() => { setPantryView("shopping"); setTab("pantry"); closeCookMode(); }}
            userId={user.id}
            family={relationships.family}
            friends={relationships.friends}
            onForkRecipe={saveUserRecipe}
            onReviseRecipe={(recipe, seedText) => setReviseSeed({ recipe, seedText })}
            resumeSessionId={cookModeResumeSessionId}
            initialFinishNow={cookModeFinishNow}
            onAddToPantry={(seed) => setMcmAddDraft({
              // Open EntryGate so user can choose scan / wizard /
              // manual / photo. The canonical seed pre-locks the
              // identity (nutmeg) so whichever pipeline they pick
              // starts with the recipe-known canonical bound.
              mode: "entry",
              name:        seed?.name || null,
              canonicalId: seed?.ingredientId || null,
              initialAmount: seed?.amount || null,
            })}
          />
        </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Post-cook AI revise sheet — seeded by CookComplete's offer
          with the cook's notes. bookRow resolves the user's OWN
          user_recipes row by slug so the smart-hybrid save applies
          (own recipe → in-place update; bundled / community cooks
          resolve null → fork with provenance). */}
      {reviseSeed && (
        <RecipeReviseSheet
          open
          onClose={() => setReviseSeed(null)}
          baseRecipe={(() => {
            const row = userRecipeRows.find((r) =>
              r.userId === user?.id && r.slug === reviseSeed.recipe?.slug);
            // Prefer the stored row's recipe (the saved version) over
            // the runtime cook object, which may carry technique-check
            // mutations applied at read time.
            return row?.recipe || reviseSeed.recipe;
          })()}
          bookRow={userRecipeRows.find((r) =>
            r.userId === user?.id && r.slug === reviseSeed.recipe?.slug) || null}
          userId={user?.id}
          seedText={reviseSeed.seedText || ""}
          onSaveInPlace={(id, nextRecipe) => updateUserRecipeContent(id, nextRecipe)}
          onForkSave={(payload, source) => saveUserRecipe(payload, source || "custom")}
        />
      )}

      {/* Post-update notification + full release-notes modal. The slim
          notification opens automatically via useWhatsNew when the
          bundled version differs from the user's last-seen; the full
          modal opens from "SEE WHAT'S NEW" or (later) Settings. */}
      {whatsNew.showNotification && (
        <WhatsNewNotification
          onSeeWhatsNew={whatsNew.openFull}
          onDismiss={whatsNew.dismiss}
        />
      )}
      {whatsNew.showFullModal && (
        <ReleaseNotesModal onClose={whatsNew.closeFull} />
      )}
      </div>
    </MCMThemeProvider>
  );
}
